import { useState, useEffect, useCallback, useRef, FormEvent, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { usePageTitle } from '@/hooks/usePageTitle';
import { podcastApi } from '@/lib/podcast-api';
import api from '@/lib/api';
import { extractInstitutionCategoriesFromApiResponse } from '@/lib/institution-categories-from-api';
import {
  localizedInstitutionCategoryDescription,
  localizedInstitutionCategoryName,
} from '@/lib/institution-category-i18n';
import { cn } from '@/lib/utils';
import { getCookie, setCookie } from '@/lib/cookies';
import { imagesApi } from '@/api/images.api';
import { resolvePodcastCoverUrl, resolveEpisodeCoverDisplayUrl } from '@/lib/podcast-cover';
import { UI_LANGUAGES } from '@/lib/ui-languages';
import ShareToGroupSection from '@/components/share/ShareToGroupSection';
import SelfAssignAsLearnerCard from '@/components/share/SelfAssignAsLearnerCard';
import PublicShareQrSection from '@/components/share/PublicShareQrSection';
import { PodcastSocialShareButtons } from '@/components/share/PodcastSocialShareButtons';
import { useAuthStore } from '@/stores/auth.store';
import { isPlatformEditorRole } from '@/lib/editor-role';
import { ShareWithInstitutionsModal } from '@/components/editor/ShareWithInstitutionsModal';
import { TrainerRightPanelHeader } from '@/components/dashboard/TrainerRightPanelHeader';
import { InstitutionStyledSelect } from '@/components/institution/InstitutionStyledSelect';
import { InstitutionStyledSearchInput } from '@/components/institution/InstitutionStyledSearchInput';
import { OptimizedImage } from '@/components/ui/OptimizedImage';
import { toast } from '@/components/ui/Toast';
import { Switch } from '@/components/ui/Switch';
import type { Episode } from '@/lib/podcast-api';
import { formatPodcastClock } from '@/lib/format-podcast-duration';
import { PodcastPdfImportModal } from '@/components/podcasts/PodcastPdfImportModal';
import { PodcastRowActions } from '@/components/podcasts/PodcastRowActions';
import { ContentDashboardActionButton } from '@/components/dashboard/ContentDashboardActionButton';
import { formateurPodcastDashboardPath } from '@/lib/content-dashboard-paths';
import { PublishPodcastOfferPanel } from '@/components/store/PublishPodcastOfferPanel';
import { isLyrraOne } from '@/lib/platform';
import { getPodcastEditPath } from '@/api/podcast-simple.api';
import {
  Plus,
  Pencil,
  Trash2,
  ListMusic,
  Mic2,
  Building2,
  X,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Loader2,
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Volume2,
  VolumeX,
  Radio,
  Share2,
  UserPlus,
  CheckCircle2,
  Copy,
  Upload,
  FolderTree,
  LayoutGrid,
  List,
  Tag,
  FileText,
} from 'lucide-react';

// ── Types ────────────────────────────────────────────────────────────────────

interface Category {
  id: string;
  name: string;
  color?: string;
  description?: string | null;
  coverImageUrl?: string | null;
  nameTranslations?: unknown;
  descriptionTranslations?: unknown;
}

/** Même convention que la liste EduFlow (navigation par catégories). */
const CATEGORY_BROWSE_UNCATEGORIZED = '__uncategorized__';

const PODCASTS_LIST_MAX_FETCH = 2000;
const PODCAST_LIST_VIEW_MODE_COOKIE = 'lyrra_podcasts_list_view_mode';
const PODCAST_LIST_CATEGORY_BROWSE_COOKIE = 'lyrra_podcasts_list_category_browse';

type PodcastListViewMode = 'grid' | 'list' | 'categories';

function readPodcastListViewModeFromCookie(): PodcastListViewMode {
  if (typeof document === 'undefined') return 'list';
  const raw = getCookie(PODCAST_LIST_VIEW_MODE_COOKIE);
  if (raw === 'grid' || raw === 'list' || raw === 'categories') return raw;
  return 'list';
}

const PODCAST_CATEGORY_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function readPodcastListCategoryBrowseFromCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const raw = getCookie(PODCAST_LIST_CATEGORY_BROWSE_COOKIE);
  if (raw == null || raw === '') return null;
  if (raw === CATEGORY_BROWSE_UNCATEGORIZED) return raw;
  const t = raw.trim();
  if (PODCAST_CATEGORY_UUID_RE.test(t)) return t;
  return null;
}

interface PodcastCategoryGroupDisplay {
  id: string;
  name: string;
  color: string;
  description: string;
  coverImageUrl: string | null;
}

interface PodcastContent {
  id: string;
  title: string;
  status: 'draft' | 'published';
  audioDurationSeconds: number | null;
  position: number;
}

interface Podcast {
  id: string;
  title: string;
  description: string;
  coverImageUrl: string | null;
  categoryId: string | null;
  categoryName: string | null;
  language: string;
  episodeSortOrder?: 'newest_first' | 'oldest_first';
  isActive: boolean;
  allowAnonymousPublicAccess?: boolean;
  isArchived: boolean;
  episodeCount: number;
  subscriberCount: number;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  fromInstitutionTrainingNeed?: boolean;
  creationMode?: 'simple' | 'advanced';
}

interface LearnerResult {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
}

interface PodcastLearnerAssignment {
  id: string;
  learnerId: string;
  learner: LearnerResult;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(dateStr: string, locale = 'fr-FR') {
  try {
    return new Date(dateStr).toLocaleDateString(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

function fmt(s: number | null) {
  if (!s) return '—';
  return formatPodcastClock(s);
}

const chipBase =
  'rounded-xl border-2 px-3.5 py-2.5 text-start text-sm font-medium transition-all duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--inst-player-primary,#0C1C38)]';
const chipInactive =
  'border-slate-200/90 bg-white text-slate-600 shadow-sm hover:border-slate-300 hover:bg-slate-50 active:scale-[0.98] dark:border-gray-600 dark:bg-gray-800/90 dark:text-slate-300 dark:hover:border-gray-500 dark:hover:bg-gray-800';
const chipActive =
  'border-[color:var(--inst-player-primary,#0C1C38)] bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_14%,transparent)] text-slate-900 shadow-[0_1px_0_rgba(15,23,42,0.06)] dark:text-white dark:shadow-none';

export type PodcastEpisodeSortOrder = 'newest_first' | 'oldest_first';

function PodcastEpisodeSortOrderSelector({
  value,
  onChange,
  t,
}: {
  value: PodcastEpisodeSortOrder;
  onChange: (order: PodcastEpisodeSortOrder) => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  const options: { id: PodcastEpisodeSortOrder; labelKey: string }[] = [
    { id: 'newest_first', labelKey: 'podcasts.episodeOrder.newestFirst' },
    { id: 'oldest_first', labelKey: 'podcasts.episodeOrder.oldestFirst' },
  ];
  return (
    <div className="space-y-2.5">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
          {t('podcasts.episodeOrder.label')}
        </p>
        <p className="mt-0.5 text-xs leading-relaxed text-slate-500 dark:text-slate-500">
          {t('podcasts.episodeOrder.help')}
        </p>
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" role="group" aria-label={t('podcasts.episodeOrder.ariaGroup')}>
        {options.map((opt) => {
          const selected = value === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => onChange(opt.id)}
              className={cn(chipBase, 'inline-flex min-w-0 items-center justify-center px-3 py-2.5 text-sm', selected ? chipActive : chipInactive)}
            >
              <span className="min-w-0 text-center font-medium">{t(opt.labelKey)}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function PodcastLanguageSelector({
  value,
  onChange,
  t,
}: {
  value: string;
  onChange: (code: string) => void;
  t: (key: string, opts?: Record<string, any>) => string;
}) {
  return (
    <div className="space-y-2.5">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
          {t('podcasts.language.label')}
        </p>
        <p className="mt-0.5 text-xs leading-relaxed text-slate-500 dark:text-slate-500">
          {t('podcasts.language.help')}
        </p>
      </div>
      <div
        className="grid grid-cols-2 gap-2 sm:grid-cols-3"
        role="group"
        aria-label={t('podcasts.language.ariaGroup')}
      >
        {UI_LANGUAGES.map((lang) => {
          const selected = value === lang.code;
          return (
            <button
              key={lang.code}
              type="button"
              onClick={() => onChange(lang.code)}
              className={cn(
                chipBase,
                'inline-flex min-w-0 items-center gap-2',
                selected ? chipActive : chipInactive,
              )}
            >
              <span className="text-[1.1rem] leading-none" aria-hidden>
                {lang.flag}
              </span>
              <span className="min-w-0 truncate text-start">{lang.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

const COVER_ACCEPT = 'image/png,image/jpeg,image/webp,image/gif';

function validatePodcastCoverFile(file: File, t: (k: string) => string): string | null {
  if (file.size > 5 * 1024 * 1024) return t('podcasts.cover.errorMaxSize');
  if (!['image/png', 'image/jpeg', 'image/webp', 'image/gif'].includes(file.type)) {
    return t('podcasts.cover.errorUnsupportedFormat');
  }
  return null;
}

function PodcastCoverDropzone({
  previewUrl,
  onFile,
  onClearReplacement,
  hasNewFile,
  t,
}: {
  previewUrl: string;
  onFile: (file: File) => void;
  onClearReplacement?: () => void;
  hasNewFile: boolean;
  t: (key: string, opts?: Record<string, any>) => string;
}) {
  const [isDragOver, setIsDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const pickFiles = (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    onFile(file);
  };

  return (
    <div className="space-y-2.5">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
          {t('podcasts.cover.sectionTitle')}
        </p>
        <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-500">
          {t('podcasts.cover.help')}
        </p>
      </div>

      <div
        role="button"
        tabIndex={0}
        aria-label={t('podcasts.cover.ariaDropzone')}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsDragOver(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsDragOver(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsDragOver(false);
          pickFiles(e.dataTransfer.files);
        }}
        onClick={() => inputRef.current?.click()}
        className={cn(
          'group relative cursor-pointer overflow-hidden rounded-2xl border-2 border-dashed transition-all duration-200 outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--inst-player-primary,#0C1C38)] focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900',
          isDragOver
            ? 'scale-[1.01] border-[color:var(--inst-player-primary,#0C1C38)] bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_12%,transparent)] shadow-[0_0_0_4px_color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_18%,transparent)]'
            : 'border-slate-200/90 bg-gradient-to-br from-slate-50/90 via-white to-slate-50/50 hover:border-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_40%,#e2e8f0)] dark:border-gray-600 dark:from-gray-800/70 dark:via-gray-900/50 dark:to-gray-800/40 dark:hover:border-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_45%,#4b5563)]',
        )}
      >
        <input
          ref={inputRef}
          type="file"
          accept={COVER_ACCEPT}
          className="hidden"
          onChange={(e) => {
            pickFiles(e.target.files);
            e.target.value = '';
          }}
        />

        {previewUrl ? (
          <div className="relative aspect-[2/1] max-h-44 w-full sm:max-h-52">
            <img src={previewUrl} alt="" className="h-full w-full object-cover" />
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-gradient-to-t from-black/65 via-black/30 to-black/10 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
              <Upload className="h-9 w-9 text-white drop-shadow-md" strokeWidth={2} aria-hidden />
              <span className="rounded-full bg-white/95 dark:bg-gray-800/95 px-3.5 py-1.5 text-xs font-semibold text-slate-800 dark:text-gray-200 shadow-lg">
                {t('podcasts.cover.replaceHint')}
              </span>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center gap-3 px-5 py-11 sm:py-12">
            <div
              className={cn(
                'flex h-14 w-14 items-center justify-center rounded-2xl transition-colors',
                isDragOver
                  ? 'bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_25%,transparent)] text-[color:var(--inst-player-primary,#0C1C38)]'
                  : 'bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_14%,transparent)] text-[color:var(--inst-player-primary,#0C1C38)]',
              )}
            >
              <Upload className="h-7 w-7" strokeWidth={2} aria-hidden />
            </div>
            <div className="text-center">
              <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                {isDragOver ? t('podcasts.cover.dropHere') : t('podcasts.cover.dragDrop')}
              </p>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                {t('podcasts.cover.or')}{' '}
                <span className="font-semibold text-[color:var(--inst-player-primary,#0C1C38)]">
                  {t('podcasts.cover.browseFiles')}
                </span>
              </p>
            </div>
            <p className="text-[11px] text-slate-400 dark:text-slate-500">{t('podcasts.cover.formatsHint')}</p>
          </div>
        )}
      </div>

      {hasNewFile && onClearReplacement ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onClearReplacement();
          }}
          className="text-xs font-medium text-slate-600 underline-offset-2 hover:text-red-600 hover:underline dark:text-slate-400"
        >
          {t('podcasts.cover.cancelNewFile')}
        </button>
      ) : null}
    </div>
  );
}

function PodcastCategorySelector({
  categories,
  value,
  onChange,
  t,
}: {
  categories: Category[];
  value: string;
  onChange: (id: string) => void;
  t: (key: string, opts?: Record<string, any>) => string;
}) {
  const { i18n } = useTranslation();
  const empty = categories.length === 0;

  return (
    <div className="space-y-2.5">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
          {t('podcasts.category.sectionTitle')}
        </p>
        <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-500">
          {empty
            ? t('podcasts.category.emptyInstitutionHint')
            : t('podcasts.category.withCategoriesHint')}
        </p>
      </div>
      <div className="flex flex-wrap gap-2" role="group" aria-label={t('podcasts.category.ariaGroup')}>
        <button
          type="button"
          onClick={() => onChange('')}
          className={cn(chipBase, value === '' ? chipActive : chipInactive)}
        >
          {t('podcasts.category.noneChip')}
        </button>
        {categories.map((c) => {
          const selected = value === c.id;
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => onChange(c.id)}
              className={cn(chipBase, 'max-w-full truncate', selected ? chipActive : chipInactive)}
              title={localizedInstitutionCategoryName(c, i18n.language)}
            >
              {localizedInstitutionCategoryName(c, i18n.language)}
            </button>
          );
        })}
      </div>
      {empty ? (
        <div className="flex gap-2 rounded-xl border border-amber-200/90 bg-gradient-to-br from-amber-50/90 to-amber-50/40 px-3 py-2.5 dark:border-amber-900/50 dark:from-amber-950/40 dark:to-amber-950/20">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
          <p className="text-xs leading-relaxed text-amber-950 dark:text-amber-100/90">
            {t('podcasts.category.emptyBanner')}
          </p>
        </div>
      ) : null}
    </div>
  );
}

// ── PodcastModal ─────────────────────────────────────────────────────────────

function PodcastModal({
  podcast,
  categories,
  defaultCategoryId,
  onClose,
  onSaved,
}: {
  podcast: Podcast | null;
  categories: Category[];
  /** Prérempli à la création si l’utilisateur est entré dans une catégorie (vue « Par catégorie »). */
  defaultCategoryId?: string;
  onClose: () => void;
  onSaved: (saved?: Podcast) => void;
}) {
  const [title, setTitle] = useState(podcast?.title ?? '');
  const [description, setDescription] = useState(podcast?.description ?? '');
  const [categoryId, setCategoryId] = useState(podcast?.categoryId ?? defaultCategoryId ?? '');
  const [language, setLanguage] = useState(podcast?.language ?? 'fr');
  const [episodeSortOrder, setEpisodeSortOrder] = useState<PodcastEpisodeSortOrder>(
    podcast?.episodeSortOrder === 'newest_first' ? 'newest_first' : 'oldest_first',
  );
  const [excerptEpisodeId, setExcerptEpisodeId] = useState(podcast?.excerptEpisodeId ?? '');
  const [excerptBlockId, setExcerptBlockId] = useState(podcast?.excerptBlockId ?? '');
  const [linkEpisodes, setLinkEpisodes] = useState<Episode[]>([]);
  const [selectedCoverFile, setSelectedCoverFile] = useState<File | null>(null);
  const [coverPreviewUrl, setCoverPreviewUrl] = useState(podcast?.coverImageUrl ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createMode, setCreateMode] = useState<'simple' | 'advanced'>(
    isLyrraOne() ? 'simple' : 'advanced',
  );
  const { t } = useTranslation();
  const isNewPodcast = !podcast;

  useEffect(() => {
    if (!podcast?.id) {
      setLinkEpisodes([]);
      return;
    }
    podcastApi
      .listEpisodes({ podcastId: podcast.id, limit: 100, status: 'published' })
      .then((res) => setLinkEpisodes(res.data ?? []))
      .catch(() => setLinkEpisodes([]));
  }, [podcast?.id]);

  const excerptBlockOptions = useMemo(() => {
    const ep = linkEpisodes.find((e) => e.id === excerptEpisodeId);
    if (!ep?.blocks?.length) return [];
    return ep.blocks
      .filter((b) => (b.text ?? '').trim() || b.recordingPath?.trim())
      .map((b, i) => ({
        id: b.id?.trim() || `block-${i}`,
        label: (b.text ?? '').trim().slice(0, 80) || t('podcasts.excerpt.recordingBlock', 'Enregistrement micro'),
      }));
  }, [linkEpisodes, excerptEpisodeId, t]);

  useEffect(() => {
    if (selectedCoverFile) {
      const url = URL.createObjectURL(selectedCoverFile);
      setCoverPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setCoverPreviewUrl(podcast?.coverImageUrl ?? '');
    return undefined;
  }, [selectedCoverFile, podcast?.coverImageUrl]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !description.trim()) {
      setError(t('podcasts.modal.errorRequiredFields'));
      return;
    }
    setSaving(true);
    setError(null);

    try {
      // Call API, then close modal
      const payload = {
        title,
        description,
        categoryId,
        categoryName: '',
        language,
        episodeSortOrder,
        ...(podcast
          ? {
              excerptEpisodeId: excerptEpisodeId.trim() || null,
              excerptBlockId: excerptBlockId.trim() || null,
            }
          : {
              creationMode: createMode,
            }),
      };
      let saved: Podcast | null = null;
      if (podcast) {
        saved = await podcastApi.updatePodcast(podcast.id, payload);
      } else {
        saved = await podcastApi.createPodcast(payload);
      }

      if (saved && selectedCoverFile) {
        await podcastApi.uploadPodcastCover(saved.id, selectedCoverFile);
      }

      onSaved(saved ?? undefined);
      onClose();
    } catch {
      setError(t('podcasts.modal.errorSaveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleCoverPick = (file: File) => {
    const msg = validatePodcastCoverFile(file, t);
    if (msg) {
      setError(msg);
      return;
    }
    setError(null);
    setSelectedCoverFile(file);
  };

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [saving, onClose]);

  const fieldCls =
    'w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-[color:var(--inst-player-primary,#0C1C38)] focus:outline-none focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_18%,transparent)] dark:border-gray-600 dark:bg-gray-800 dark:text-white';

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex justify-end rtl:justify-start"
      role="dialog"
      aria-modal="true"
      aria-labelledby="podcast-modal-panel-title"
    >
      <div
        className="absolute inset-0 bg-slate-900/45 backdrop-blur-[2px] dark:bg-black/55"
        onClick={() => !saving && onClose()}
        aria-hidden
      />
      <div className="relative z-10 flex h-full w-full max-w-md flex-col border-s border-slate-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-900 ltr:animate-slide-in-right rtl:animate-slide-in-left">
        <TrainerRightPanelHeader
          icon={Mic2}
          title={podcast ? t('podcasts.modal.titleEdit') : t('podcasts.modal.titleNew')}
          titleId="podcast-modal-panel-title"
          description={
            podcast
              ? podcast.title
              : t('podcasts.modal.subtitleNew')
          }
          onClose={onClose}
          closeDisabled={saving}
        />

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-5 sm:px-6">
            {error && (
              <div className="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">
                <AlertCircle className="h-4 w-4 shrink-0" />
                {error}
              </div>
            )}

            {isNewPodcast ? (
              <div className="space-y-2">
                <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  {t('podcasts.createMode.label', 'Type de podcast')}
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setCreateMode('simple')}
                    className={cn(
                      'rounded-xl border p-3 text-left text-sm transition-colors',
                      createMode === 'simple'
                        ? 'border-[color:var(--inst-player-primary,#0C1C38)] bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_8%,white)] ring-2 ring-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_22%,transparent)] dark:bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_18%,transparent)]'
                        : 'border-slate-200 hover:border-slate-300 dark:border-gray-700 dark:hover:border-gray-600',
                    )}
                  >
                    <p className="font-semibold text-slate-900 dark:text-white">
                      {t('podcasts.createMode.simpleTitle', 'Podcast simple')}
                    </p>
                    <p className="mt-1 text-xs leading-relaxed text-slate-500 dark:text-gray-400">
                      {t(
                        'podcasts.createMode.simpleHint',
                        'Titre, description et épisodes enregistrés directement dans le podcast.',
                      )}
                    </p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setCreateMode('advanced')}
                    className={cn(
                      'rounded-xl border p-3 text-left text-sm transition-colors',
                      createMode === 'advanced'
                        ? 'border-[color:var(--inst-player-primary,#0C1C38)] bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_8%,white)] ring-2 ring-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_22%,transparent)] dark:bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_18%,transparent)]'
                        : 'border-slate-200 hover:border-slate-300 dark:border-gray-700 dark:hover:border-gray-600',
                    )}
                  >
                    <p className="font-semibold text-slate-900 dark:text-white">
                      {t('podcasts.createMode.advancedTitle', 'Podcast complexe')}
                    </p>
                    <p className="mt-1 text-xs leading-relaxed text-slate-500 dark:text-gray-400">
                      {t(
                        'podcasts.createMode.advancedHint',
                        'Bibliothèque d’épisodes, IA, chapitres, musique et partage multi-podcasts.',
                      )}
                    </p>
                  </button>
                </div>
              </div>
            ) : podcast.creationMode ? (
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800/60">
                <span className="text-slate-500 dark:text-gray-400">
                  {t('podcasts.createMode.current', 'Type :')}{' '}
                </span>
                <span className="font-medium text-slate-900 dark:text-white">
                  {podcast.creationMode === 'simple'
                    ? t('podcasts.createMode.simpleTitle', 'Podcast simple')
                    : t('podcasts.createMode.advancedTitle', 'Podcast complexe')}
                </span>
              </div>
            ) : null}

            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">{t('podcasts.form.titleLabel')}</label>
              <input
                type="text"
                maxLength={200}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t('podcasts.form.namePlaceholder')}
                autoFocus
                className={fieldCls}
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">{t('podcasts.form.descriptionLabel')}</label>
              <textarea
                maxLength={5000}
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('podcasts.form.descriptionPlaceholder')}
                className={cn(fieldCls, 'resize-none')}
              />
            </div>

            <PodcastCoverDropzone
              previewUrl={coverPreviewUrl}
              onFile={handleCoverPick}
              hasNewFile={Boolean(selectedCoverFile)}
              onClearReplacement={() => setSelectedCoverFile(null)}
              t={t}
            />

            <div className="space-y-5 rounded-2xl border border-slate-200/90 bg-gradient-to-b from-slate-50/90 to-white p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.7)] dark:border-gray-700/90 dark:from-gray-800/50 dark:to-gray-900/40 dark:shadow-none sm:p-5">
              <PodcastCategorySelector
                categories={categories}
                value={categoryId}
                onChange={setCategoryId}
                t={t}
              />
              <div className="border-t border-slate-200/80 pt-5 dark:border-gray-700/80">
                <PodcastLanguageSelector value={language} onChange={setLanguage} t={t} />
              </div>
              <div className="border-t border-slate-200/80 pt-5 dark:border-gray-700/80">
                <PodcastEpisodeSortOrderSelector value={episodeSortOrder} onChange={setEpisodeSortOrder} t={t} />
              </div>
            </div>

            {podcast ? (
              <div className="space-y-3 rounded-2xl border border-slate-200/90 bg-white p-4 dark:border-gray-700 dark:bg-gray-900/40">
                <div>
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
                    {t('podcasts.excerpt.sectionTitle', 'Extrait public')}
                  </h3>
                  <p className="mt-1 text-xs leading-relaxed text-slate-500 dark:text-gray-400">
                    {t(
                      'podcasts.excerpt.sectionHint',
                      'Les visiteurs pourront écouter ce passage avant de lancer le podcast. L’audio de l’épisode doit être généré.',
                    )}
                  </p>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-300">
                    {t('podcasts.excerpt.episodeLabel', 'Épisode')}
                  </label>
                  <select
                    value={excerptEpisodeId}
                    onChange={(e) => {
                      setExcerptEpisodeId(e.target.value);
                      setExcerptBlockId('');
                    }}
                    className={fieldCls}
                  >
                    <option value="">{t('podcasts.excerpt.noExcerpt', 'Aucun extrait')}</option>
                    {linkEpisodes.map((ep) => (
                      <option key={ep.id} value={ep.id}>
                        {ep.title}
                      </option>
                    ))}
                  </select>
                </div>
                {excerptEpisodeId ? (
                  <div>
                    <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-300">
                      {t('podcasts.excerpt.blockLabel', 'Bloc / passage')}
                    </label>
                    <select
                      value={excerptBlockId}
                      onChange={(e) => setExcerptBlockId(e.target.value)}
                      className={fieldCls}
                    >
                      <option value="">{t('podcasts.excerpt.chooseBlock', 'Choisir un bloc…')}</option>
                      {excerptBlockOptions.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.label}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-slate-200 bg-slate-50/90 px-5 py-4 dark:border-gray-700 dark:bg-gray-900/80 sm:flex-row sm:justify-end sm:gap-3 sm:px-6">
            <button
              type="button"
              onClick={() => !saving && onClose()}
              disabled={saving}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-slate-200 dark:hover:bg-gray-700"
            >
              {t('podcasts.actions.cancel')}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-[color:var(--inst-player-primary,#0C1C38)] px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-[color:var(--inst-player-primary-hover,#19335E)] disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {saving ? t('podcasts.actions.saving') : podcast ? t('podcasts.actions.save') : t('podcasts.actions.create')}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

// ── PodcastEditPanel ─────────────────────────────────────────────────────────

function PodcastEditPanel({
  podcast,
  categories,
  onClose,
  onSaved,
}: {
  podcast: Podcast;
  categories: Category[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [title, setTitle] = useState(podcast.title);
  const [description, setDescription] = useState(podcast.description);
  const [categoryId, setCategoryId] = useState(podcast.categoryId ?? '');
  const [language, setLanguage] = useState(podcast.language ?? 'fr');
  const [episodeSortOrder, setEpisodeSortOrder] = useState<PodcastEpisodeSortOrder>(
    podcast.episodeSortOrder === 'newest_first' ? 'newest_first' : 'oldest_first',
  );
  const [selectedCoverFile, setSelectedCoverFile] = useState<File | null>(null);
  const [coverPreviewUrl, setCoverPreviewUrl] = useState(podcast.coverImageUrl ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    setEpisodeSortOrder(podcast.episodeSortOrder === 'newest_first' ? 'newest_first' : 'oldest_first');
  }, [podcast.id, podcast.episodeSortOrder]);

  useEffect(() => {
    if (selectedCoverFile) {
      const url = URL.createObjectURL(selectedCoverFile);
      setCoverPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setCoverPreviewUrl(podcast.coverImageUrl ?? '');
    return undefined;
  }, [selectedCoverFile, podcast.coverImageUrl]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !description.trim()) {
      setError(t('podcasts.modal.errorRequiredFields'));
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await podcastApi.updatePodcast(podcast.id, {
        title,
        description,
        categoryId,
        categoryName: '',
        language,
        episodeSortOrder,
      });
      if (selectedCoverFile) {
        await podcastApi.uploadPodcastCover(podcast.id, selectedCoverFile);
      }
      setSuccess(true);
      onSaved();
      setTimeout(() => setSuccess(false), 2000);
    } catch {
      setError(t('podcasts.modal.errorSaveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleCoverPick = (file: File) => {
    const msg = validatePodcastCoverFile(file, t);
    if (msg) {
      setError(msg);
      return;
    }
    setError(null);
    setSelectedCoverFile(file);
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex justify-end rtl:justify-start">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/20 backdrop-blur-[2px]" onClick={onClose} />

      {/* Panel */}
      <div className="relative flex w-full max-w-md flex-col border-s border-slate-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-900 ltr:animate-slide-in-right rtl:animate-slide-in-left">
        <TrainerRightPanelHeader
          icon={Mic2}
          title={t('podcasts.editPanel.headerTitle')}
          description={podcast.title}
          onClose={onClose}
          closeDisabled={saving}
        />
        <div className="shrink-0 border-b border-slate-200 bg-white px-5 py-4 dark:border-gray-700 dark:bg-gray-900">
          <PodcastCoverDropzone
            previewUrl={coverPreviewUrl}
            onFile={handleCoverPick}
            hasNewFile={Boolean(selectedCoverFile)}
            onClearReplacement={() => setSelectedCoverFile(null)}
            t={t}
          />
        </div>

        {/* Form body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="space-y-5 px-5 py-5">
            {error && (
              <div className="flex items-center gap-2 rounded-lg bg-red-50 dark:bg-red-900/20 px-3 py-2.5 text-sm text-red-700 dark:text-red-300">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {error}
              </div>
            )}
            {success && (
              <div className="flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_10%,transparent)]" style={{ color: 'var(--inst-player-primary, #0C1C38)' }}>
                <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
                {t('podcasts.editPanel.successSaved')}
              </div>
            )}

            {/* Title */}
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('podcasts.form.titleLabel')}</label>
              <input
                type="text"
                maxLength={200}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t('podcasts.form.namePlaceholder')}
                className="w-full rounded-lg border border-slate-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2.5 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:border-[color:var(--inst-player-primary,#0C1C38)] focus:outline-none focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_15%,transparent)] transition-colors"
              />
            </div>

            {/* Description */}
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('podcasts.form.descriptionLabel')}</label>
              <textarea
                maxLength={5000}
                rows={4}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('podcasts.form.descriptionPlaceholder')}
                className="w-full rounded-lg border border-slate-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2.5 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:border-[color:var(--inst-player-primary,#0C1C38)] focus:outline-none focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_15%,transparent)] transition-colors resize-none"
              />
            </div>

            <div className="space-y-5 rounded-2xl border border-slate-200/90 bg-gradient-to-b from-slate-50/90 to-white p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.7)] dark:border-gray-700/90 dark:from-gray-800/50 dark:to-gray-900/40 dark:shadow-none sm:p-5">
              <PodcastCategorySelector
                categories={categories}
                value={categoryId}
                onChange={setCategoryId}
                t={t}
              />
              <div className="border-t border-slate-200/80 pt-5 dark:border-gray-700/80">
                <PodcastLanguageSelector value={language} onChange={setLanguage} t={t} />
              </div>
              <div className="border-t border-slate-200/80 pt-5 dark:border-gray-700/80">
                <PodcastEpisodeSortOrderSelector value={episodeSortOrder} onChange={setEpisodeSortOrder} t={t} />
              </div>
            </div>

            {/* Info card */}
            <div className="rounded-lg border border-slate-100 dark:border-gray-700 bg-slate-50/50 dark:bg-gray-800/50 p-3">
              <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
                <span>{t('podcasts.editPanel.statsEpisodes')}{podcast.episodeCount}</span>
                <span>{t('podcasts.editPanel.statsSubscribers')}{podcast.subscriberCount}</span>
              </div>
              <div className="mt-1.5 flex items-center justify-between text-xs text-slate-400 dark:text-slate-500">
                <span>{t('podcasts.editPanel.createdOn')}{formatDate(podcast.createdAt, i18n.language)}</span>
                <span className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-xs font-medium ${
                  podcast.isActive
                    ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                    : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
                }`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${podcast.isActive ? 'bg-green-500' : 'bg-gray-400'}`} />
                  {podcast.isActive ? t('podcasts.status.active') : t('podcasts.status.inactive')}
                </span>
              </div>
            </div>
          </div>

          {/* Footer actions */}
          <div className="sticky bottom-0 flex items-center justify-end gap-3 border-t border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-5 py-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-slate-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-gray-700 transition-colors"
            >
              {t('podcasts.actions.close')}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 rounded-lg px-5 py-2 text-sm font-medium text-white shadow-sm transition-colors disabled:opacity-50"
              style={{ background: 'var(--inst-player-primary, #0C1C38)' }}
              onMouseEnter={(e) => { if (!saving) e.currentTarget.style.background = 'var(--inst-player-primary-hover, #19335E)'; }}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--inst-player-primary, #0C1C38)')}
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {saving ? t('podcasts.editPanel.saving') : t('podcasts.actions.save')}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

// ── ContentsPanel ────────────────────────────────────────────────────────────

function ContentsPanel({
  podcast,
  onClose,
  onChanged,
}: {
  podcast: Podcast;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [contents, setContents] = useState<PodcastContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [allContents, setAllContents] = useState<PodcastContent[]>([]);
  const [addSearch, setAddSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [showPdfImport, setShowPdfImport] = useState(false);
  const { t } = useTranslation();

  const loadContents = useCallback(async () => {
    setLoading(true);
    try {
      const result = await podcastApi.listEpisodes({ podcastId: podcast.id });
      setContents(result.data.map((ep, i) => ({
        id: ep.id,
        title: ep.title,
        status: ep.status,
        audioDurationSeconds: ep.audioDurationSeconds,
        position: i + 1,
      })));
    } catch {
      setContents([]);
    } finally {
      setLoading(false);
    }
  }, [podcast.id, podcast.episodeCount]);

  const loadAllContents = useCallback(async () => {
    try {
      const result = await podcastApi.listEpisodes({ limit: 100 });
      setAllContents(result.data.map((ep, i) => ({
        id: ep.id,
        title: ep.title,
        status: ep.status,
        audioDurationSeconds: ep.audioDurationSeconds,
        position: i + 1,
      })));
    } catch {
      setAllContents([]);
    }
  }, []);

  useEffect(() => {
    loadContents();
    loadAllContents();
  }, [loadContents, loadAllContents]);

  const linkedIds = new Set(contents.map((c) => c.id));
  const available = allContents.filter(
    (c) => !linkedIds.has(c.id) && (!addSearch || c.title.toLowerCase().includes(addSearch.toLowerCase()))
  );

  const handleAssign = async (contentId: string) => {
    const item = allContents.find((c) => c.id === contentId);
    if (item) {
      try {
        await podcastApi.assignEpisode(contentId, podcast.id);
      } catch { /* ignore */ }
      setContents((prev) => [...prev, { ...item, position: prev.length + 1 }]);
      onChanged();
    }
  };

  const handleRemove = async (contentId: string) => {
    try {
      await podcastApi.unassignEpisode(contentId, podcast.id);
    } catch { /* ignore */ }
    setContents((prev) => prev.filter((c) => c.id !== contentId));
    onChanged();
  };

  return createPortal(
    <div className="fixed inset-y-0 end-0 z-50 flex w-full max-w-md flex-col border-s border-slate-200 bg-white shadow-xl dark:border-[hsl(217,33%,22%)] dark:bg-[hsl(217,32%,15%)]">
      <TrainerRightPanelHeader
        icon={ListMusic}
        title={t('podcasts.episodesPanel.title')}
        description={`${podcast.title} · ${t('podcasts.episodesPanel.episodeCount', { count: contents.length })}${podcast.categoryName ? ` · ${podcast.categoryName}` : ''}`}
        onClose={onClose}
      />

      {/* Add section */}
      <div className="border-b border-slate-200 dark:border-gray-700 px-5 py-3 space-y-2">
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setShowAdd(!showAdd)}
            className="flex items-center gap-2 rounded-lg bg-[color:var(--inst-player-primary,#0C1C38)] px-3 py-1.5 text-sm font-medium text-white hover:bg-[color:var(--inst-player-primary-hover,#19335E)]"
          >
            <Plus className="h-4 w-4" />
            {t('podcasts.episodesPanel.addEpisode')}
          </button>
          <button
            type="button"
            onClick={() => setShowPdfImport(true)}
            className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-800 hover:bg-slate-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
          >
            <FileText className="h-4 w-4" />
            {t('podcasts.pdfImport.openButton')}
          </button>
        </div>

        {showAdd && (
          <div className="mt-3 space-y-2">
            <div className="min-w-0">
              <InstitutionStyledSearchInput
                value={addSearch}
                onChange={(e) => setAddSearch(e.target.value)}
                placeholder={t('podcasts.episodesPanel.searchPlaceholder')}
              />
            </div>
            <div className="max-h-48 space-y-1 overflow-y-auto">
              {available.length === 0 ? (
                <p className="py-2 text-center text-sm text-slate-400 dark:text-gray-500">{t('podcasts.episodesPanel.noEpisodeAvailable')}</p>
              ) : (
                available.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => handleAssign(c.id)}
                    className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-start hover:bg-slate-50 dark:hover:bg-gray-800"
                  >
                    <div>
                      <span className="text-sm font-medium text-slate-900 dark:text-white">{c.title}</span>
                      <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-gray-400">
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                          c.status === 'published' ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'
                        }`}>
                          {c.status === 'published' ? t('podcasts.episodeStatus.published') : t('podcasts.episodeStatus.draft')}
                        </span>
                        {c.audioDurationSeconds && <span>{fmt(c.audioDurationSeconds)}</span>}
                      </div>
                    </div>
                    <Plus className="h-4 w-4 text-slate-400 dark:text-gray-500" />
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Contents list */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="space-y-3 p-5">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-14 animate-pulse rounded-lg bg-slate-100 dark:bg-gray-800" />
            ))}
          </div>
        ) : contents.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <ListMusic className="h-10 w-10 text-slate-300 dark:text-gray-600" />
            <p className="mt-3 text-sm font-medium text-slate-900 dark:text-white">{t('podcasts.episodesPanel.emptyTitle')}</p>
            <p className="text-sm text-slate-500 dark:text-gray-400">{t('podcasts.episodesPanel.emptySubtitle')}</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100 dark:divide-gray-700">
            {contents.map((c, i) => (
              <div key={c.id} className="flex items-center gap-3 px-5 py-3 hover:bg-slate-50 dark:hover:bg-gray-800">
                <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_10%,transparent)] text-xs font-semibold text-[color:var(--inst-player-primary,#0C1C38)]">
                  {i + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="truncate text-sm font-medium text-slate-900 dark:text-white">{c.title}</p>
                  <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-gray-400">
                    <span className={`rounded-full px-2 py-0.5 font-medium ${
                      c.status === 'published' ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'
                    }`}>
                      {c.status === 'published' ? t('podcasts.episodeStatus.published') : t('podcasts.episodeStatus.draft')}
                    </span>
                    {c.audioDurationSeconds && <span>{fmt(c.audioDurationSeconds)}</span>}
                  </div>
                </div>
                <Link
                  to={`/podcasts/episodes/${c.id}`}
                  className="rounded-lg p-1.5 text-slate-400 dark:text-gray-500 hover:bg-slate-100 dark:hover:bg-gray-700 hover:text-[color:var(--inst-player-primary,#0C1C38)]"
                  title={t('podcasts.actions.edit')}
                >
                  <Pencil className="h-4 w-4" />
                </Link>
                <button
                  onClick={() => handleRemove(c.id)}
                  className="rounded-lg p-1.5 text-slate-400 dark:text-gray-500 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600"
                  title={t('podcasts.episodesPanel.remove')}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      {showPdfImport ? (
        <PodcastPdfImportModal
          podcast={podcast}
          onClose={() => setShowPdfImport(false)}
          onImported={() => {
            void loadContents();
            onChanged();
          }}
        />
      ) : null}
    </div>,
    document.body
  );
}

// ── PodcastPreviewPanel ──────────────────────────────────────────────────────

function PodcastPreviewPanel({
  podcast,
  onClose,
}: {
  podcast: Podcast;
  onClose: () => void;
}) {
  const [episodes, setEpisodes] = useState<Episode[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState<number | null>(null);
  const { t } = useTranslation();
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [autoNext, setAutoNext] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);
  const barsRef = useRef<number[]>([]);

  if (barsRef.current.length === 0) {
    barsRef.current = Array.from({ length: 32 }, () => 15 + Math.random() * 85);
  }
  const bars = barsRef.current;

  useEffect(() => {
    setLoading(true);
    podcastApi
      .listEpisodes({ podcastId: podcast.id, limit: 100 })
      .then((r) => setEpisodes(r.data))
      .catch(() => setEpisodes([]))
      .finally(() => setLoading(false));
  }, [podcast.id]);

  const audioUrl = (idx: number | null) => {
    if (idx === null) return null;
    const fp = episodes[idx]?.audioFilePath;
    if (!fp) return null;
    return `/uploads/tts/${fp.split('/').pop()}`;
  };

  const loadAndPlay = useCallback(
    (idx: number) => {
      const a = audioRef.current;
      if (!a) return;
      const url = audioUrl(idx);
      if (!url) return;
      a.src = url;
      a.playbackRate = speed;
      setCurrentIndex(idx);
      setProgress(0);
      setDuration(0);
      setPlaying(true);
      a.load();
      a.play().catch(() => {});
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [episodes, speed],
  );

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
    } else {
      if (!a.src && currentIndex !== null) loadAndPlay(currentIndex);
      else a.play().catch(() => {});
    }
  };

  const goNext = useCallback(() => {
    if (currentIndex === null) return;
    const next = episodes.findIndex((ep, i) => i > currentIndex && !!ep.audioFilePath);
    if (next >= 0) loadAndPlay(next);
    else setPlaying(false);
  }, [currentIndex, episodes, loadAndPlay]);

  const goPrev = () => {
    if (currentIndex === null) return;
    for (let i = currentIndex - 1; i >= 0; i--) {
      if (episodes[i]?.audioFilePath) {
        loadAndPlay(i);
        return;
      }
    }
  };

  const playAll = () => {
    const first = episodes.findIndex((ep) => !!ep.audioFilePath);
    if (first >= 0) {
      setAutoNext(true);
      loadAndPlay(first);
    }
  };

  const handleEnded = () => {
    if (autoNext) goNext();
    else setPlaying(false);
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (audioRef.current) audioRef.current.currentTime = Number(e.target.value);
  };

  const changeSpeed = (s: number) => {
    if (audioRef.current) audioRef.current.playbackRate = s;
    setSpeed(s);
  };

  const playbackSpeedOptions = useMemo(
    () => [0.5, 0.75, 1, 1.25, 1.5, 2].map((s) => ({ value: String(s), label: `${s}x` })),
    [],
  );

  const fmtTime = (s: number) => formatPodcastClock(s);

  const currentEpisode = currentIndex !== null ? episodes[currentIndex] : null;
  const hasAudioEpisodes = episodes.some((ep) => !!ep.audioFilePath);

  return createPortal(
    <div className="fixed inset-0 z-50 flex justify-end rtl:justify-start">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/20 backdrop-blur-[2px]" onClick={onClose} />

      {/* Panel */}
      <div className="relative flex w-full max-w-lg flex-col border-s border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-900">
      <TrainerRightPanelHeader
        icon={Radio}
        title={t('podcasts.preview.title')}
        description={
          [podcast.title, podcast.categoryName, podcast.description].filter(Boolean).join(' · ') ||
          t('podcasts.preview.fallbackDescription')
        }
        onClose={onClose}
      />

      <div className="flex shrink-0 items-center gap-3 border-b border-gray-200 px-5 py-3 dark:border-gray-800">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_10%,transparent)]">
          <img src={resolvePodcastCoverUrl(podcast.coverImageUrl)} alt="" className="h-full w-full object-cover" />
        </div>
        <p className="line-clamp-2 min-w-0 flex-1 text-xs text-gray-500 dark:text-gray-400">{podcast.description || '—'}</p>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-800 px-5 py-3 flex-shrink-0">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {loading ? '…' : t('podcasts.preview.episodeCount', { count: episodes.length })}
        </p>
        <button
          onClick={playAll}
          disabled={!hasAudioEpisodes || loading}
          className="flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-sm font-medium text-white transition-colors disabled:opacity-40"
          style={{ background: 'var(--inst-player-primary, #0C1C38)' }}
          onMouseEnter={(e) => { if (hasAudioEpisodes) e.currentTarget.style.background = 'var(--inst-player-primary-hover, #19335E)'; }}
          onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--inst-player-primary, #0C1C38)')}
        >
          <Radio className="h-3.5 w-3.5" />
          {t('podcasts.preview.playAll')}
        </button>
      </div>

      {/* Episodes list */}
      <div className="relative flex-1 overflow-y-auto">
        <div
          className="pointer-events-none absolute inset-0 bg-cover bg-center opacity-40"
          style={{
            backgroundImage: `url(${resolveEpisodeCoverDisplayUrl(currentEpisode?.coverImageUrl, podcast.coverImageUrl)})`,
          }}
          aria-hidden
        />
        <div className="relative z-10">
          {loading ? (
            <div className="space-y-3 p-5">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-14 animate-pulse rounded-lg bg-gray-100 dark:bg-gray-800" />
              ))}
            </div>
          ) : episodes.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <ListMusic className="h-10 w-10 text-gray-300" />
              <p className="mt-3 text-sm font-medium text-gray-900 dark:text-white">{t('podcasts.preview.emptyTitle')}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('podcasts.preview.emptySubtitle')}</p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {episodes.map((ep, i) => {
                const isActive = currentIndex === i;
                const hasAudio = !!ep.audioFilePath;
                return (
                  <div
                    key={ep.id}
                    className={`flex items-center gap-3 px-5 py-3 transition-colors ${
                      isActive
                        ? 'bg-white/85 dark:bg-gray-900/70'
                        : 'bg-white/70 dark:bg-gray-900/60 hover:bg-white/85 dark:hover:bg-gray-900/75'
                    }`}
                  >
                    <button
                      onClick={() => {
                        if (!hasAudio) return;
                        if (isActive) toggle();
                        else { setAutoNext(false); loadAndPlay(i); }
                      }}
                      disabled={!hasAudio}
                      className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full transition-all ${
                        isActive && playing
                          ? 'bg-[color:var(--inst-player-primary,#0C1C38)] text-white shadow-md'
                          : hasAudio
                            ? 'bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_10%,transparent)] text-[color:var(--inst-player-primary,#0C1C38)] hover:bg-[color:var(--inst-player-primary,#0C1C38)] hover:text-white'
                            : 'bg-gray-100 text-gray-300 cursor-not-allowed dark:bg-gray-800'
                      }`}
                      title={hasAudio ? (isActive && playing ? t('podcasts.player.pause') : t('podcasts.player.listen')) : t('podcasts.player.noAudio')}
                    >
                      {isActive && playing
                        ? <Pause className="h-3.5 w-3.5" />
                        : <Play className="ms-0.5 h-3.5 w-3.5" />}
                    </button>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-3">
                        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl border border-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_25%,transparent)] bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_12%,transparent)]">
                          <img
                            src={resolveEpisodeCoverDisplayUrl(ep.coverImageUrl, podcast.coverImageUrl)}
                            alt=""
                            className="h-full w-full object-cover"
                          />
                        </div>

                        <div className="min-w-0 flex-1">
                          <p
                            className={`truncate text-sm font-medium ${
                              isActive
                                ? 'text-[color:var(--inst-player-primary,#0C1C38)]'
                                : 'text-gray-900 dark:text-white'
                            }`}
                          >
                            {ep.title}
                          </p>
                          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                            <span
                              className={`rounded-full px-1.5 py-0.5 font-medium ${
                                ep.status === 'published'
                                  ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                  : 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                              }`}
                            >
                              {ep.status === 'published' ? t('podcasts.episodeStatus.published') : t('podcasts.episodeStatus.draft')}
                            </span>
                            {ep.audioDurationSeconds && <span>{fmtTime(ep.audioDurationSeconds)}</span>}
                            {!hasAudio && <span className="text-gray-300 dark:text-gray-600 italic">{t('podcasts.player.noAudio')}</span>}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Animated bars when playing */}
                    {isActive && playing && (
                      <div className="flex items-end gap-0.5 h-5 flex-shrink-0">
                        {[40, 70, 55, 85, 45].map((h, b) => (
                          <div
                            key={b}
                            className="w-0.5 rounded-sm bg-[color:var(--inst-player-primary,#0C1C38)]"
                            style={{
                              height: `${h}%`,
                              animation: `pulse ${0.5 + b * 0.15}s ease-in-out infinite alternate`,
                            }}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Player bar */}
      <div className={`border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-5 py-4 flex-shrink-0 transition-all duration-300 ${currentEpisode ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>
        <audio
          ref={audioRef}
          onLoadedMetadata={() => { if (audioRef.current) setDuration(audioRef.current.duration); }}
          onTimeUpdate={() => { if (audioRef.current) setProgress(audioRef.current.currentTime); }}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={handleEnded}
          muted={muted}
        />

        {/* Waveform */}
        <div className="mb-3 flex h-10 items-end gap-px">
          {bars.map((h, i) => {
            const isPlayed = duration > 0 && i / bars.length <= progress / duration;
            return (
              <div
                key={i}
                className={`flex-1 rounded-t-sm transition-colors duration-75 ${
                  isPlayed ? 'bg-[color:var(--inst-player-primary,#0C1C38)]' : 'bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_20%,transparent)] dark:bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_10%,transparent)]'
                }`}
                style={{ height: `${h}%` }}
              />
            );
          })}
        </div>

        {/* Seek bar */}
        <input
          type="range"
          min={0}
          max={duration || 1}
          value={progress}
          onChange={handleSeek}
          className="mb-1 w-full accent-[color:var(--inst-player-primary,#0C1C38)] cursor-pointer"
        />
        <div className="mb-3 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
          <span>{fmtTime(progress)}</span>
          {currentEpisode && (
            <p className="truncate max-w-[180px] font-medium text-gray-700 dark:text-gray-200 text-center">
              {currentEpisode.title}
            </p>
          )}
          <span>{fmtTime(duration)}</span>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-between gap-2">
          <div className="w-24 shrink-0">
            <InstitutionStyledSelect
              label={false}
              panelZIndexClass="z-[220]"
              options={playbackSpeedOptions}
              value={String(speed)}
              onChange={(v) => changeSpeed(Number(v))}
            />
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={goPrev}
              disabled={currentIndex === null || currentIndex === 0}
              className="rounded-full p-1.5 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 transition-colors"
            >
              <SkipBack className="h-4 w-4" />
            </button>
            <button
              onClick={toggle}
              disabled={!currentEpisode}
              className="flex h-10 w-10 items-center justify-center rounded-full text-white shadow-md transition-colors disabled:opacity-40"
              style={{ background: 'var(--inst-player-primary, #0C1C38)' }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--inst-player-primary-hover, #19335E)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--inst-player-primary, #0C1C38)')}
            >
              {playing ? <Pause className="h-5 w-5" /> : <Play className="ms-0.5 h-5 w-5" />}
            </button>
            <button
              onClick={goNext}
              disabled={currentIndex === null || currentIndex >= episodes.length - 1}
              className="rounded-full p-1.5 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 transition-colors"
            >
              <SkipForward className="h-4 w-4" />
            </button>
          </div>

          <button
            onClick={() => setMuted(!muted)}
            className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-1.5 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          </button>
        </div>
      </div>
    </div>
    </div>,
    document.body
  );
}

// ── DeleteConfirmDialog ──────────────────────────────────────────────────────

function DeleteConfirmDialog({
  podcast,
  onCancel,
  onConfirm,
}: {
  podcast: Podcast;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-sm rounded-xl border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 shadow-xl">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">{t('podcasts.deleteDialog.title')}</h3>
        <p className="mt-2 text-sm text-slate-500">
          {t('podcasts.deleteDialog.body', { title: podcast.title })}
        </p>
        <div className="mt-5 flex justify-end gap-3">
          <button onClick={onCancel} className="rounded-lg border border-slate-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-4 py-2 text-sm font-medium text-slate-700 dark:text-gray-300 hover:bg-slate-50 dark:hover:bg-gray-700">
            {t('podcasts.actions.cancel')}
          </button>
          <button onClick={onConfirm} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700">
            {t('podcasts.deleteDialog.confirm')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/** Normalise la réponse GET `/podcasts/:id/public-sharing-stats` (enveloppes `data` variables). */
function parsePublicSharingStatsResponse(resp: unknown): {
  shareClicks: { linkedin: number; facebook: number; twitter: number };
  publicPageVisits: number;
  subscribers: Array<{
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    company: string | null;
    subscribedAt: string;
  }>;
} | null {
  const r = resp as Record<string, unknown> | null | undefined;
  if (!r || typeof r !== 'object') return null;
  let payload: Record<string, unknown> | null = null;
  const d0 = r.data;
  if (d0 && typeof d0 === 'object') {
    const d = d0 as Record<string, unknown>;
    if (d.shareClicks && typeof d.shareClicks === 'object' && Array.isArray(d.subscribers)) {
      payload = d;
    } else if (d.data && typeof d.data === 'object') {
      const dd = d.data as Record<string, unknown>;
      if (dd.shareClicks && typeof dd.shareClicks === 'object' && Array.isArray(dd.subscribers)) {
        payload = dd;
      }
    }
  }
  if (!payload && r.shareClicks && typeof r.shareClicks === 'object' && Array.isArray(r.subscribers)) {
    payload = r as Record<string, unknown>;
  }
  if (!payload?.shareClicks || typeof payload.shareClicks !== 'object' || !Array.isArray(payload.subscribers)) {
    return null;
  }
  const sc = payload.shareClicks as Record<string, unknown>;
  const visitsRaw = payload.publicPageVisits ?? payload.public_page_visits;
  return {
    shareClicks: {
      linkedin: Number(sc.linkedin) || 0,
      facebook: Number(sc.facebook) || 0,
      twitter: Number(sc.twitter) || 0,
    },
    publicPageVisits: typeof visitsRaw === 'number' && Number.isFinite(visitsRaw) ? visitsRaw : Number(visitsRaw) || 0,
    subscribers: payload.subscribers as Array<{
      id: string;
      firstName: string;
      lastName: string;
      email: string;
      company: string | null;
      subscribedAt: string;
    }>,
  };
}

function SharePodcastModal({
  podcast,
  onClose,
}: {
  podcast: Podcast;
  onClose: () => void;
}) {
  const user = useAuthStore((s) => s.user);
  if (isPlatformEditorRole(user?.role)) {
    return (
      <ShareWithInstitutionsModal
        contentType="podcast"
        contentId={podcast.id}
        contentTitle={podcast.title}
        onClose={onClose}
      />
    );
  }
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<LearnerResult[]>([]);
  const [searching, setSearching] = useState(false);
  const { t } = useTranslation();
  const [assigningId, setAssigningId] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [message, setMessage] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const requestSeq = useRef(0);
  const [assignedLearners, setAssignedLearners] = useState<PodcastLearnerAssignment[]>([]);
  const [loadingAssigned, setLoadingAssigned] = useState(false);
  const [removingLearnerId, setRemovingLearnerId] = useState<string | null>(null);
  const [publicShareUrl, setPublicShareUrl] = useState('');
  const [loadingPublicShareUrl, setLoadingPublicShareUrl] = useState(false);
  const [copiedPublicLink, setCopiedPublicLink] = useState(false);
  const [allowAnonPublic, setAllowAnonPublic] = useState(Boolean(podcast.allowAnonymousPublicAccess));
  const [savingAllowAnon, setSavingAllowAnon] = useState(false);
  type PublicSharingSubscriber = {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    company: string | null;
    subscribedAt: string;
  };
  type PublicSharingInsights = {
    shareClicks: { linkedin: number; facebook: number; twitter: number };
    publicPageVisits: number;
    subscribers: PublicSharingSubscriber[];
  };
  const [publicInsights, setPublicInsights] = useState<PublicSharingInsights | null>(null);
  const [loadingPublicInsights, setLoadingPublicInsights] = useState(false);

  const refreshAssignedLearners = async () => {
    setLoadingAssigned(true);
    try {
      const resp: any = await api.get(`/podcasts/${podcast.id}/learners`);
      const payload = resp?.data ?? resp;
      const list = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : [];
      setAssignedLearners(list);
    } catch {
      setAssignedLearners([]);
    } finally {
      setLoadingAssigned(false);
    }
  };

  const refreshAvailableLearners = async (currentSearch = search) => {
    const resp: any = await api.get(`/podcasts/${podcast.id}/learners/available`, { search: currentSearch });
    const payload = resp?.data ?? resp;
    const list = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.data)
        ? payload.data
        : [];
    setResults(list);
  };

  const removeAssignedLearner = async (learnerId: string) => {
    setRemovingLearnerId(learnerId);
    setMessage(null);
    try {
      await api.delete(`/podcasts/${podcast.id}/learners/${learnerId}`);
      setMessage({ type: 'ok', text: t('podcasts.share.messageAccessRemoved') });
      await Promise.all([refreshAssignedLearners(), refreshAvailableLearners()]);
    } catch (err: any) {
      setMessage({ type: 'err', text: err?.message ?? t('podcasts.share.errorRemoveLearner') });
    } finally {
      setRemovingLearnerId(null);
    }
  };

  useEffect(() => {
    if (!allowAnonPublic) {
      void refreshAssignedLearners();
    }
  }, [podcast.id, allowAnonPublic]);

  const loadPublicSharingInsights = useCallback(async () => {
    if (!allowAnonPublic) return;
    setLoadingPublicInsights(true);
    try {
      const resp: unknown = await api.get(`/podcasts/${podcast.id}/public-sharing-stats`);
      const parsed = parsePublicSharingStatsResponse(resp);
      if (parsed) {
        setPublicInsights(parsed);
      } else {
        setPublicInsights(null);
      }
    } catch {
      setPublicInsights(null);
    } finally {
      setLoadingPublicInsights(false);
    }
  }, [allowAnonPublic, podcast.id]);

  useEffect(() => {
    if (!allowAnonPublic) {
      setPublicInsights(null);
      return;
    }
    void loadPublicSharingInsights();
  }, [allowAnonPublic, loadPublicSharingInsights]);

  useEffect(() => {
    if (!allowAnonPublic) return;
    let debounceTimer: ReturnType<typeof setTimeout> | undefined;
    const scheduleRefresh = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        void loadPublicSharingInsights();
      }, 400);
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') scheduleRefresh();
    };
    window.addEventListener('focus', scheduleRefresh);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      window.removeEventListener('focus', scheduleRefresh);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [allowAnonPublic, loadPublicSharingInsights]);

  useEffect(() => {
    setAllowAnonPublic(Boolean(podcast.allowAnonymousPublicAccess));
  }, [podcast.id, podcast.allowAnonymousPublicAccess]);

  useEffect(() => {
    let cancelled = false;
    const loadPublicShareLink = async () => {
      setLoadingPublicShareUrl(true);
      try {
        const resp: any = await api.post(`/podcasts/${podcast.id}/public-share-link`);
        const payload = resp?.data ?? resp;
        if (!cancelled) setPublicShareUrl(typeof payload?.url === 'string' ? payload.url : '');
      } catch {
        if (!cancelled) setPublicShareUrl('');
      } finally {
        if (!cancelled) setLoadingPublicShareUrl(false);
      }
    };
    void loadPublicShareLink();
    return () => {
      cancelled = true;
    };
  }, [podcast.id]);

  useEffect(() => {
    if (allowAnonPublic) return;
    const timer = setTimeout(async () => {
      const seq = ++requestSeq.current;
      setSearching(true);
      try {
        const resp: any = await api.get(`/podcasts/${podcast.id}/learners/available`, { search });
        const payload = resp?.data ?? resp;
        const list = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.data)
            ? payload.data
            : [];
        if (seq === requestSeq.current) setResults(list);
      } catch {
        if (seq === requestSeq.current) setResults([]);
      } finally {
        if (seq === requestSeq.current) setSearching(false);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [search, podcast.id, allowAnonPublic]);

  const assignExisting = async (learner: LearnerResult) => {
    setAssigningId(learner.id);
    setMessage(null);
    try {
      await api.post(`/podcasts/${podcast.id}/learners`, {
        learnerId: learner.id,
      });
      setMessage({ type: 'ok', text: t('podcasts.share.messageShared') });
      await Promise.all([refreshAvailableLearners(), refreshAssignedLearners()]);
    } catch (err: any) {
      setMessage({ type: 'err', text: err?.message ?? t('podcasts.share.errorShare') });
    } finally {
      setAssigningId(null);
    }
  };

  const createAndAssign = async () => {
    const valid = firstName.trim() && lastName.trim() && /\S+@\S+\.\S+/.test(email);
    if (!valid) return;
    setCreating(true);
    setMessage(null);
    try {
      await api.post(`/podcasts/${podcast.id}/learners`, {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim().toLowerCase(),
      });
      setMessage({ type: 'ok', text: t('podcasts.share.messageCreatedAndShared') });
      setFirstName('');
      setLastName('');
      setEmail('');
      setShowCreateForm(false);
      await Promise.all([refreshAvailableLearners(), refreshAssignedLearners()]);
    } catch (err: any) {
      setMessage({ type: 'err', text: err?.message ?? t('podcasts.share.errorCreateLearner') });
    } finally {
      setCreating(false);
    }
  };

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex justify-end rtl:justify-start">
      <button
        type="button"
        aria-label={t('podcasts.actions.close')}
        onClick={onClose}
        className="absolute inset-0 bg-black/40"
      />
      <div className="relative z-10 flex h-full w-full max-w-xl flex-col border-s border-slate-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-900">
        <TrainerRightPanelHeader
          icon={Share2}
          title={t('podcasts.share.panelTitle')}
          description={podcast.title}
          onClose={onClose}
          supportFabPanelMaxWidth="xl"
        />

        <div className="flex-1 space-y-4 overflow-y-auto p-5">
          <div className="rounded-xl border border-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_25%,transparent)] bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_7%,transparent)] p-3">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-gray-300">
              {t('podcasts.share.publicLinkSection')}
            </p>
            <div className="mb-3 flex cursor-pointer items-start gap-2 rounded-lg border border-slate-200/80 bg-white/60 p-2 dark:border-slate-600/50 dark:bg-slate-900/40">
              <Switch
                size="sm"
                className="mt-0.5 shrink-0"
                checked={allowAnonPublic}
                disabled={savingAllowAnon}
                onChange={async (v) => {
                  setSavingAllowAnon(true);
                  try {
                    await api.put(`/podcasts/${podcast.id}`, { allowAnonymousPublicAccess: v });
                    setAllowAnonPublic(v);
                  } catch {
                    setAllowAnonPublic(!v);
                  } finally {
                    setSavingAllowAnon(false);
                  }
                }}
              />
              <span className="text-xs text-slate-700 dark:text-gray-200">
                {t(
                  'podcasts.share.allowAnonymousPublic',
                  'Autoriser l’écoute sans compte Lyrra (lien direct). Sinon, connexion obligatoire puis accès au podcast.',
                )}
              </span>
            </div>
            {loadingPublicShareUrl ? (
              <p className="text-xs text-slate-500 dark:text-gray-400">{t('podcasts.share.generatingLink')}</p>
            ) : publicShareUrl ? (
              <div className="space-y-2">
                <a
                  href={publicShareUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block truncate text-xs text-[color:var(--inst-player-primary,#0C1C38)] underline"
                >
                  {publicShareUrl}
                </a>
                <button
                  type="button"
                  onClick={async () => {
                    await navigator.clipboard.writeText(publicShareUrl);
                    setCopiedPublicLink(true);
                    window.setTimeout(() => setCopiedPublicLink(false), 1200);
                  }}
                  className="inline-flex items-center gap-1 rounded-md bg-[color:var(--inst-player-primary,#0C1C38)] px-2.5 py-1.5 text-xs font-medium text-white hover:bg-[color:var(--inst-player-primary-hover,#19335E)]"
                >
                  <Copy className="h-3.5 w-3.5" />
                  {copiedPublicLink ? t('podcasts.share.copied') : t('podcasts.share.copyLink')}
                </button>
                {allowAnonPublic ? (
                  <PodcastSocialShareButtons url={publicShareUrl} title={podcast.title} className="pt-2" />
                ) : null}
                <PublicShareQrSection url={publicShareUrl} downloadBasename={`podcast-${podcast.id}`} />
              </div>
            ) : (
              <p className="text-xs text-red-600 dark:text-red-300">{t('podcasts.share.errorPublicLink')}</p>
            )}
          </div>

          {message && (
            <div className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm ${
              message.type === 'ok'
                ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300'
                : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300'
            }`}>
              {message.type === 'ok' ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
              {message.text}
            </div>
          )}

          {allowAnonPublic ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4 dark:border-gray-700 dark:bg-gray-800/40">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-gray-300">
                {t('podcasts.share.publicShareStatsHeading')}
              </p>
              {loadingPublicInsights ? (
                <p className="flex items-center gap-1 text-xs text-slate-400 dark:text-gray-500">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  {t('podcasts.share.publicShareStatsLoading')}
                </p>
              ) : publicInsights ? (
                <>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                    <div className="rounded-lg border border-slate-200 bg-white px-2 py-2 text-center dark:border-gray-600 dark:bg-gray-900">
                      <p className="text-lg font-bold text-slate-900 dark:text-white tabular-nums">
                        {publicInsights.publicPageVisits}
                      </p>
                      <p className="text-[10px] font-medium text-slate-500 dark:text-gray-400">
                        {t('podcasts.share.publicShareStatsVisits')}
                      </p>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-white px-2 py-2 text-center dark:border-gray-600 dark:bg-gray-900">
                      <p className="text-lg font-bold text-slate-900 dark:text-white tabular-nums">
                        {publicInsights.shareClicks.linkedin}
                      </p>
                      <p className="text-[10px] font-medium text-slate-500 dark:text-gray-400">
                        {t('podcasts.share.publicShareStatsLinkedIn')}
                      </p>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-white px-2 py-2 text-center dark:border-gray-600 dark:bg-gray-900">
                      <p className="text-lg font-bold text-slate-900 dark:text-white tabular-nums">
                        {publicInsights.shareClicks.facebook}
                      </p>
                      <p className="text-[10px] font-medium text-slate-500 dark:text-gray-400">
                        {t('podcasts.share.publicShareStatsFacebook')}
                      </p>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-white px-2 py-2 text-center dark:border-gray-600 dark:bg-gray-900">
                      <p className="text-lg font-bold text-slate-900 dark:text-white tabular-nums">
                        {publicInsights.shareClicks.twitter}
                      </p>
                      <p className="text-[10px] font-medium text-slate-500 dark:text-gray-400">
                        {t('podcasts.share.publicShareStatsTwitter')}
                      </p>
                    </div>
                  </div>
                  <p className="mb-2 mt-5 text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-gray-300">
                    {t('podcasts.share.publicSubscribersHeading')}
                  </p>
                  {publicInsights.subscribers.length === 0 ? (
                    <p className="text-xs text-slate-400 dark:text-gray-500">{t('podcasts.share.publicSubscribersEmpty')}</p>
                  ) : (
                    <div className="max-h-56 space-y-2 overflow-y-auto rounded-lg border border-slate-200 bg-white p-2 dark:border-gray-600 dark:bg-gray-900">
                      {publicInsights.subscribers.map((s) => (
                        <div
                          key={s.id}
                          className="rounded-md border border-slate-100 px-2 py-2 dark:border-gray-700"
                        >
                          <p className="truncate text-sm font-medium text-slate-900 dark:text-white">
                            {[s.firstName, s.lastName].filter(Boolean).join(' ') || s.email}
                          </p>
                          <p className="truncate text-xs text-slate-500 dark:text-gray-400">{s.email}</p>
                          {s.company ? (
                            <p className="truncate text-xs text-slate-500 dark:text-gray-500">
                              {t('podcasts.share.publicSubscribersCompany')}: {s.company}
                            </p>
                          ) : null}
                          <p className="mt-0.5 text-[10px] text-slate-400 dark:text-gray-500">
                            {new Date(s.subscribedAt).toLocaleString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <p className="text-xs text-slate-400 dark:text-gray-500">{t('podcasts.share.publicShareStatsUnavailable')}</p>
              )}
            </div>
          ) : (
            <>
              <SelfAssignAsLearnerCard
                kind="podcasts"
                resourceId={podcast.id}
                assignedUserIds={assignedLearners.map((a) => a.learnerId)}
                onAssigned={async () => {
                  await Promise.all([refreshAssignedLearners(), refreshAvailableLearners()]);
                }}
              />
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-gray-300">
                  {t('podcasts.share.selectExistingLearnerLabel')}
                </label>
                <div className="min-w-0">
                  <InstitutionStyledSearchInput
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t('podcasts.share.searchLearnerPlaceholder')}
                  />
                </div>
                <div className="mt-2 max-h-52 overflow-y-auto rounded-lg border border-slate-200 p-2 dark:border-gray-700">
                  {searching ? (
                    <p className="flex items-center gap-1 text-xs text-slate-400 dark:text-gray-500">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      {t('podcasts.share.loadingLearners')}
                    </p>
                  ) : results.length === 0 ? (
                    <p className="text-xs text-slate-400">{t('podcasts.share.noLearnerAvailable')}</p>
                  ) : (
                    <div className="space-y-1">
                      {results.map((r) => (
                        <div
                          key={r.id}
                          className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2 dark:bg-gray-800"
                        >
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-slate-900 dark:text-white">
                              {[r.firstName, r.lastName].filter(Boolean).join(' ') || r.email}
                            </p>
                            <p className="truncate text-xs text-slate-500 dark:text-gray-400">{r.email}</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => assignExisting(r)}
                            disabled={assigningId === r.id}
                            className="inline-flex items-center gap-1 rounded-md bg-[color:var(--inst-player-primary,#0C1C38)] px-2.5 py-1 text-xs font-medium text-white hover:bg-[color:var(--inst-player-primary-hover,#19335E)] disabled:opacity-60"
                          >
                            {assigningId === r.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Share2 className="h-3 w-3" />}
                            {t('podcasts.actions.share')}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="border-t border-slate-200 pt-3 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => setShowCreateForm((v) => !v)}
                  className="inline-flex items-center gap-1 text-sm font-medium text-[color:var(--inst-player-primary,#0C1C38)] hover:text-[color:var(--inst-player-primary-hover,#19335E)]"
                >
                  <UserPlus className="h-4 w-4" />
                  {showCreateForm ? t('podcasts.share.hideQuickCreate') : t('podcasts.share.showQuickCreate')}
                </button>

                {showCreateForm && (
                  <div className="mt-3 space-y-2 rounded-lg border border-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_20%,transparent)] bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_5%,transparent)] p-3 dark:bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_10%,transparent)]">
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        placeholder={t('podcasts.share.firstNamePlaceholder')}
                        className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                      />
                      <input
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        placeholder={t('podcasts.share.lastNamePlaceholder')}
                        className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                      />
                    </div>
                    <input
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t('podcasts.share.emailPlaceholder')}
                      type="email"
                      className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                    />
                    <button
                      type="button"
                      onClick={() => void createAndAssign()}
                      disabled={creating}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[color:var(--inst-player-primary,#0C1C38)] px-3 py-2 text-sm font-medium text-white hover:bg-[color:var(--inst-player-primary-hover,#19335E)] disabled:opacity-60"
                    >
                      {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
                      {t('podcasts.share.submitCreateAndInvite')}
                    </button>
                  </div>
                )}
              </div>

              <ShareToGroupSection
                assignGroupUrl={`/podcasts/${podcast.id}/learners/group`}
                onAssigned={() => {
                  void refreshAssignedLearners();
                  void refreshAvailableLearners();
                }}
              />

              <div className="border-t border-slate-200 pt-3 dark:border-gray-700">
                <p className="mb-2 text-sm font-medium text-slate-700 dark:text-gray-300">
                  {t('podcasts.share.assignedLearnersHeading', { count: assignedLearners.length })}
                </p>
                {loadingAssigned ? (
                  <p className="text-xs text-slate-400">{t('podcasts.share.loadingAssigned')}</p>
                ) : assignedLearners.length === 0 ? (
                  <p className="text-xs text-slate-400">{t('podcasts.share.noAssignedLearners')}</p>
                ) : (
                  <div className="max-h-48 space-y-2 overflow-y-auto">
                    {assignedLearners.map((a) => (
                      <div
                        key={a.id}
                        className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2 dark:border-gray-700 dark:bg-gray-800"
                      >
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-slate-900 dark:text-white">
                            {[a.learner.firstName, a.learner.lastName].filter(Boolean).join(' ') || a.learner.email}
                          </p>
                          <p className="truncate text-xs text-slate-500 dark:text-gray-400">{a.learner.email}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => void removeAssignedLearner(a.learnerId)}
                          disabled={removingLearnerId === a.learnerId}
                          className="rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50 dark:hover:bg-red-900/20"
                          title={t('podcasts.share.removeAccessTitle')}
                        >
                          {removingLearnerId === a.learnerId ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

function resolvePodcastCategoryLabel(
  p: Podcast,
  categoryNameById: Map<string, string>,
  uncategorizedLabel: string,
): string {
  return (
    (p.categoryName && p.categoryName.trim().length > 0
      ? p.categoryName
      : p.categoryId
        ? categoryNameById.get(p.categoryId) ?? ''
        : '') || uncategorizedLabel
  );
}

type PodcastRowHandlers = {
  onCoverLightbox: (url: string, title: string) => void;
  onPreview: () => void;
  onContents: () => void;
  onImportDocument: () => void;
  onShare: () => void;
  onOpenOffer?: () => void;
  onEdit: () => void;
  onToggleActive: () => void;
  onArchive: () => void;
  onRestoreArchive: () => void;
  onDelete: () => void;
  onOpenDashboard?: () => void;
};

function PodcastTrainerListRow({
  podcast: p,
  categoryLabel,
  showCategoryChip,
  t,
  i18n,
  onCoverLightbox,
  onPreview,
  onContents,
  onImportDocument,
  onShare,
  onOpenOffer,
  onEdit,
  onToggleActive,
  onArchive,
  onRestoreArchive,
  onDelete,
  onOpenDashboard,
}: {
  podcast: Podcast;
  categoryLabel: string;
  showCategoryChip: boolean;
  t: TFunction;
  i18n: { language: string };
} & PodcastRowHandlers) {
  return (
    <div
      className="group flex cursor-pointer items-center gap-4 rounded-xl border border-slate-200/60 bg-white p-4 transition-all duration-200 hover:border-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_30%,transparent)] hover:shadow-md dark:border-gray-700 dark:bg-gray-900"
      onClick={onEdit}
    >
      <span
        className={cn('h-2.5 w-2.5 shrink-0 rounded-full', p.isActive ? 'bg-green-500' : 'bg-gray-400')}
        aria-hidden
      />

      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onCoverLightbox(resolvePodcastCoverUrl(p.coverImageUrl), p.title);
        }}
        className="flex h-12 w-12 min-h-12 min-w-12 shrink-0 cursor-zoom-in items-center justify-center self-center overflow-hidden rounded-xl border border-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_25%,transparent)] bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_12%,transparent)] shadow-[0_8px_24px_rgba(15,23,42,0.08)] transition-transform hover:scale-[1.02] dark:border-gray-700/80 dark:bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_10%,transparent)]"
        aria-label={t('podcasts.list.coverPreviewAria', { title: p.title })}
      >
        <OptimizedImage
          src={resolvePodcastCoverUrl(p.coverImageUrl)}
          alt=""
          className="h-full w-full max-h-none max-w-none object-cover"
        />
      </button>

      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <p className="truncate text-sm font-semibold text-slate-900 dark:text-white">{p.title}</p>
          {p.fromInstitutionTrainingNeed ? (
            <span
              className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-900 dark:bg-amber-950/50 dark:text-amber-200"
              title={t('podcasts.list.enterpriseNeedTitle')}
            >
              <Building2 className="h-3 w-3" />
              {t('podcasts.list.enterpriseBadge')}
            </span>
          ) : null}
          {showCategoryChip ? (
            <span
              className="flex-shrink-0 rounded-full px-2 py-0.5 text-xs font-medium bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_10%,transparent)]"
              style={{ color: 'var(--inst-player-primary, #0C1C38)' }}
            >
              {categoryLabel}
            </span>
          ) : null}
          {p.isArchived ? (
            <span className="inline-flex shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
              {t('podcasts.list.badgeArchived')}
            </span>
          ) : null}
        </div>
        {p.description ? (
          <p className="mt-0.5 truncate text-xs text-slate-500 dark:text-gray-400">{p.description}</p>
        ) : null}
      </div>

      <span
        className={cn(
          'hidden shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium md:inline-flex',
          p.isActive
            ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300'
            : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
        )}
      >
        {p.isActive ? t('podcasts.status.active') : t('podcasts.status.inactive')}
      </span>

      <div className="hidden shrink-0 items-center gap-4 text-xs text-slate-500 dark:text-gray-400 sm:flex">
        <span>{t('podcasts.list.episodesShort', { count: p.episodeCount })}</span>
        <span>{t('podcasts.list.subscribersShort', { count: p.subscriberCount.toLocaleString() })}</span>
        <span>{formatDate(p.createdAt, i18n.language)}</span>
      </div>

      <div className="flex shrink-0 items-center gap-1" onClick={(e) => e.stopPropagation()}>
        {onOpenDashboard ? <ContentDashboardActionButton onClick={onOpenDashboard} /> : null}
        <PodcastRowActions
          isArchived={Boolean(p.isArchived)}
          isActive={p.isActive}
          onPreview={onPreview}
          onContents={onContents}
          onImportDocument={onImportDocument}
          onShare={onShare}
          onOpenOffer={onOpenOffer}
          onEdit={onEdit}
          onToggleActive={onToggleActive}
          onArchive={onArchive}
          onRestoreArchive={onRestoreArchive}
          onDelete={onDelete}
          onOpenDashboard={onOpenDashboard}
        />
      </div>
    </div>
  );
}

function PodcastTrainerGridCard({
  podcast: p,
  categoryLabel,
  t,
  i18n,
  onCoverLightbox,
  onPreview,
  onContents,
  onImportDocument,
  onShare,
  onOpenOffer,
  onEdit,
  onToggleActive,
  onArchive,
  onRestoreArchive,
  onDelete,
  onOpenDashboard,
}: {
  podcast: Podcast;
  categoryLabel: string;
  t: TFunction;
  i18n: { language: string };
} & PodcastRowHandlers) {
  return (
    <div className="flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm transition-all hover:border-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_30%,transparent)] hover:shadow-md dark:border-slate-700 dark:bg-slate-900/70">
      <button
        type="button"
        onClick={() => onCoverLightbox(resolvePodcastCoverUrl(p.coverImageUrl), p.title)}
        className="relative aspect-[16/10] w-full overflow-hidden border-b border-slate-100 bg-slate-50 dark:border-gray-800 dark:bg-gray-800"
        aria-label={t('podcasts.list.coverPreviewAria', { title: p.title })}
      >
        <OptimizedImage
          src={resolvePodcastCoverUrl(p.coverImageUrl)}
          alt=""
          className="h-full w-full object-cover transition-transform duration-300 hover:scale-[1.02]"
        />
      </button>
      <div className="flex min-h-0 flex-1 flex-col gap-2 p-3">
        <div className="flex items-start justify-between gap-2">
          <p className="line-clamp-2 min-w-0 flex-1 text-sm font-semibold text-gray-900 dark:text-white">{p.title}</p>
          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            {onOpenDashboard ? <ContentDashboardActionButton onClick={onOpenDashboard} /> : null}
            <PodcastRowActions
              isArchived={Boolean(p.isArchived)}
              isActive={p.isActive}
              onPreview={onPreview}
              onContents={onContents}
              onImportDocument={onImportDocument}
              onShare={onShare}
              onOpenOffer={onOpenOffer}
              onEdit={onEdit}
              onToggleActive={onToggleActive}
              onArchive={onArchive}
              onRestoreArchive={onRestoreArchive}
              onDelete={onDelete}
              onOpenDashboard={onOpenDashboard}
            />
          </div>
        </div>
        <span
          className="inline-flex w-fit max-w-full truncate rounded-full px-2 py-0.5 text-[11px] font-medium bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_10%,transparent)]"
          style={{ color: 'var(--inst-player-primary, #0C1C38)' }}
        >
          {categoryLabel}
        </span>
        {p.fromInstitutionTrainingNeed ? (
          <span className="inline-flex w-fit items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-900 dark:bg-amber-950/50 dark:text-amber-200">
            <Building2 className="h-3 w-3 shrink-0" />
            {t('podcasts.list.enterpriseBadge')}
          </span>
        ) : null}
        {p.isArchived ? (
          <span className="inline-flex w-fit rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
            {t('podcasts.list.badgeArchived')}
          </span>
        ) : null}
        <p className="line-clamp-2 text-xs text-gray-500 dark:text-gray-400">{p.description}</p>
        <div className="mt-auto flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-2 text-xs text-gray-500 dark:border-gray-800 dark:text-gray-400">
          <span>{t('podcasts.list.episodesShort', { count: p.episodeCount })}</span>
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium ${
              p.isActive
                ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
            }`}
          >
            {p.isActive ? t('podcasts.status.active') : t('podcasts.status.inactive')}
          </span>
        </div>
        <p className="text-[11px] text-gray-400 dark:text-gray-500">{formatDate(p.createdAt, i18n.language)}</p>
      </div>
    </div>
  );
}

function PodcastImportPickerModal({
  podcasts,
  onSelect,
  onClose,
}: {
  podcasts: Podcast[];
  onSelect: (p: Podcast) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [q, setQ] = useState('');

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return podcasts;
    return podcasts.filter((p) => p.title.toLowerCase().includes(needle));
  }, [podcasts, q]);

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
      <div className="flex max-h-[80vh] w-full max-w-md flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-900">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4 dark:border-gray-700">
          <h2 className="text-lg font-bold text-slate-900 dark:text-white">{t('podcasts.pdfImport.pickPodcastTitle')}</h2>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-gray-800" aria-label={t('podcasts.actions.close')}>
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="px-5 py-3">
          <InstitutionStyledSearchInput
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('podcasts.filters.searchPlaceholder')}
          />
        </div>
        <ul className="flex-1 overflow-y-auto divide-y divide-slate-100 dark:divide-gray-800">
          {filtered.length === 0 ? (
            <li className="px-5 py-8 text-center text-sm text-slate-500">{t('podcasts.pdfImport.noPodcastMatch')}</li>
          ) : (
            filtered.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  onClick={() => onSelect(p)}
                  className="flex w-full items-center gap-3 px-5 py-3 text-start hover:bg-slate-50 dark:hover:bg-gray-800"
                >
                  <FileText className="h-4 w-4 shrink-0 text-[color:var(--inst-player-primary,#0C1C38)]" />
                  <span className="truncate text-sm font-medium text-slate-900 dark:text-white">{p.title}</span>
                </button>
              </li>
            ))
          )}
        </ul>
      </div>
    </div>,
    document.body,
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────

export default function PodcastsPage({ embedded = false }: { embedded?: boolean } = {}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  usePageTitle(t('podcasts.title', 'Podcasts'));
  const [searchParams, setSearchParams] = useSearchParams();
  const [podcasts, setPodcasts] = useState<Podcast[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<PodcastListViewMode>(() => readPodcastListViewModeFromCookie());
  /** « Tous » = hors archives ; « Archivés » uniquement. */
  const [activeFilter, setActiveFilter] = useState<'all' | 'archived'>('all');
  const [categoryBrowseKey, setCategoryBrowseKey] = useState<string | null>(() =>
    readPodcastListCategoryBrowseFromCookie(),
  );
  const [modalPodcast, setModalPodcast] = useState<Podcast | null | 'new'>(null);
  const [editPanelPodcast, setEditPanelPodcast] = useState<Podcast | null>(null);
  const [contentsPanel, setContentsPanel] = useState<Podcast | null>(null);
  const [importDocumentPodcast, setImportDocumentPodcast] = useState<Podcast | null>(null);
  const [importPickerOpen, setImportPickerOpen] = useState(false);
  const [previewPanel, setPreviewPanel] = useState<Podcast | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<Podcast | null>(null);
  const [sharePodcast, setSharePodcast] = useState<Podcast | null>(null);
  const [offerPodcast, setOfferPodcast] = useState<Podcast | null>(null);

  const openPodcastWorkspace = useCallback(
    (podcast: Podcast) => {
      if (podcast.creationMode === 'simple') {
        navigate(getPodcastEditPath(podcast));
        return;
      }
      setContentsPanel(podcast);
    },
    [navigate],
  );

  const openPodcastSettings = useCallback(
    (podcast: Podcast) => {
      if (podcast.creationMode === 'simple') {
        navigate(getPodcastEditPath(podcast));
        return;
      }
      setEditPanelPodcast(podcast);
    },
    [navigate],
  );

  const openPodcastDashboard = useCallback(
    (podcast: Podcast) => {
      navigate(formateurPodcastDashboardPath(podcast.id));
    },
    [navigate],
  );

  const [coverLightbox, setCoverLightbox] = useState<{ url: string; title: string } | null>(null);

  const pageSize = 20;

  const loadInstitutionCategories = useCallback(async () => {
    try {
      const response = await api.get<unknown>('/institution/categories');
      const rows = extractInstitutionCategoriesFromApiResponse(response);
      const normalized = rows
        .map((c: any) => ({
          id: String(c.id),
          name: String(c.name ?? '').trim(),
          color: String(c.color ?? '#94a3b8').trim() || '#94a3b8',
          description: typeof c.description === 'string' ? c.description : '',
          coverImageUrl:
            typeof c.coverImageUrl === 'string' && c.coverImageUrl.trim().length > 0 ? c.coverImageUrl.trim() : null,
          nameTranslations: c.nameTranslations,
          descriptionTranslations: c.descriptionTranslations,
        }))
        .filter((c: Category) => c.id && c.name.length > 0);
      setCategories(normalized);
    } catch {
      setCategories([]);
    }
  }, []);

  const loadPodcasts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await podcastApi.listPodcasts({
        search: search || undefined,
        page: 1,
        limit: PODCASTS_LIST_MAX_FETCH,
      });
      setPodcasts(
        (result.data ?? []).map((p) => ({
          ...p,
          isArchived: Boolean(p.isArchived),
        })),
      );
      setTotal(result.total);
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'message' in e ? String((e as { message: unknown }).message) : '';
      setError(msg || t('podcasts.list.loadError'));
      setPodcasts([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [search, t]);

  useEffect(() => {
    loadPodcasts();
  }, [loadPodcasts]);

  useEffect(() => {
    loadInstitutionCategories();
  }, [loadInstitutionCategories]);

  useEffect(() => {
    const editId = (searchParams.get('edit') ?? '').trim();
    if (!editId) return;
    const podcast = podcasts.find((p) => p.id === editId);
    if (!podcast) return;
    if (podcast.creationMode === 'simple') {
      navigate(getPodcastEditPath(podcast));
    } else {
      setEditPanelPodcast(podcast);
    }
    const next = new URLSearchParams(searchParams);
    next.delete('edit');
    setSearchParams(next, { replace: true });
  }, [navigate, searchParams, setSearchParams, podcasts]);

  useEffect(() => {
    if (!coverLightbox) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setCoverLightbox(null);
    };
    window.addEventListener('keydown', onKey);

    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [coverLightbox]);

  const handleDelete = async (podcast: Podcast) => {
    try {
      await podcastApi.deletePodcast(podcast.id);
    } catch {
      // Still remove from UI
    }
    setPodcasts((prev) => prev.filter((p) => p.id !== podcast.id));
    setTotal((prev) => prev - 1);
    setDeleteConfirm(null);
  };

  const handleToggleActive = async (podcast: Podcast) => {
    if (podcast.isArchived) return;
    try {
      await podcastApi.updatePodcast(podcast.id, { isActive: !podcast.isActive });
    } catch {
      // Still toggle in UI
    }
    setPodcasts((prev) =>
      prev.map((p) => (p.id === podcast.id ? { ...p, isActive: !p.isActive } : p))
    );
  };

  const handleArchivePodcast = async (podcast: Podcast) => {
    if (podcast.isArchived) return;
    setPodcasts((prev) => prev.map((p) => (p.id === podcast.id ? { ...p, isArchived: true } : p)));
    try {
      await podcastApi.updatePodcast(podcast.id, { isArchived: true });
      toast.success(t('podcasts.list.archiveSuccess'));
    } catch {
      setPodcasts((prev) => prev.map((p) => (p.id === podcast.id ? { ...p, isArchived: false } : p)));
      toast.error(t('podcasts.list.archiveError'));
    }
  };

  const handleRestoreArchivedPodcast = async (podcast: Podcast) => {
    if (!podcast.isArchived) return;
    setPodcasts((prev) => prev.map((p) => (p.id === podcast.id ? { ...p, isArchived: false } : p)));
    try {
      await podcastApi.updatePodcast(podcast.id, { isArchived: false });
      toast.success(t('podcasts.list.restoreSuccess'));
    } catch {
      setPodcasts((prev) => prev.map((p) => (p.id === podcast.id ? { ...p, isArchived: true } : p)));
      toast.error(t('podcasts.list.restoreError'));
    }
  };

  const filteredPodcasts = useMemo(() => {
    if (activeFilter === 'all') return podcasts.filter((p) => !p.isArchived);
    return podcasts.filter((p) => p.isArchived);
  }, [podcasts, activeFilter]);

  const categoryNameById = useMemo(
    () => new Map(categories.map((c) => [c.id, localizedInstitutionCategoryName(c, i18n.language)] as const)),
    [categories, i18n.language],
  );

  const groupedByCategory = useMemo(() => {
    const metaById = new Map(categories.map((c) => [c.id, c] as const));
    const map = new Map<string, { category: PodcastCategoryGroupDisplay; podcasts: Podcast[] }>();
    const uncategorized: Podcast[] = [];

    for (const p of filteredPodcasts) {
      const cid = p.categoryId?.trim();
      if (!cid) {
        uncategorized.push(p);
        continue;
      }
      if (!map.has(cid)) {
        const meta = metaById.get(cid);
        const fallbackName = (p.categoryName && p.categoryName.trim()) || meta?.name || cid;
        const merged = {
          name: meta?.name ?? fallbackName,
          nameTranslations: meta?.nameTranslations,
          description: typeof meta?.description === 'string' ? meta.description : '',
          descriptionTranslations: meta?.descriptionTranslations,
        };
        map.set(cid, {
          category: {
            id: cid,
            name: localizedInstitutionCategoryName(merged, i18n.language),
            color: meta?.color ?? '#94a3b8',
            description: localizedInstitutionCategoryDescription(
              {
                description: (typeof meta?.description === 'string' ? meta.description : '').trim() || null,
                descriptionTranslations: merged.descriptionTranslations,
                name: merged.name,
                nameTranslations: merged.nameTranslations,
              },
              i18n.language,
            ),
            coverImageUrl: meta?.coverImageUrl ?? null,
          },
          podcasts: [],
        });
      }
      map.get(cid)!.podcasts.push(p);
    }

    for (const opt of categories) {
      if (!map.has(opt.id)) {
        map.set(opt.id, {
          category: {
            id: opt.id,
            name: localizedInstitutionCategoryName(opt, i18n.language),
            color: opt.color ?? '#94a3b8',
            description: localizedInstitutionCategoryDescription(opt, i18n.language),
            coverImageUrl: opt.coverImageUrl ?? null,
          },
          podcasts: [],
        });
      }
    }

    const groups = Array.from(map.values()).sort((a, b) =>
      a.category.name.localeCompare(b.category.name, i18n.language, { sensitivity: 'base' }),
    );
    return { groups, uncategorized };
  }, [filteredPodcasts, categories, i18n.language]);

  const categoryKeyForUi = viewMode === 'categories' ? categoryBrowseKey : null;

  useEffect(() => {
    setCookie(PODCAST_LIST_VIEW_MODE_COOKIE, viewMode);
  }, [viewMode]);

  useEffect(() => {
    if (categoryBrowseKey === null) setCookie(PODCAST_LIST_CATEGORY_BROWSE_COOKIE, '');
    else setCookie(PODCAST_LIST_CATEGORY_BROWSE_COOKIE, categoryBrowseKey);
  }, [categoryBrowseKey]);

  const activeCategoryBrowse = useMemo(() => {
    if (categoryKeyForUi === null) return null;
    if (categoryKeyForUi === CATEGORY_BROWSE_UNCATEGORIZED) {
      return {
        category: null as PodcastCategoryGroupDisplay | null,
        podcasts: groupedByCategory.uncategorized,
      };
    }
    const g = groupedByCategory.groups.find((x) => x.category.id === categoryKeyForUi);
    if (!g) return null;
    return { category: g.category, podcasts: g.podcasts };
  }, [categoryKeyForUi, groupedByCategory]);

  useEffect(() => {
    if (categoryBrowseKey === null || categoryBrowseKey === CATEGORY_BROWSE_UNCATEGORIZED) return;
    const exists = groupedByCategory.groups.some((g) => g.category.id === categoryBrowseKey);
    if (!exists) setCategoryBrowseKey(null);
  }, [categoryBrowseKey, groupedByCategory.groups]);

  const totalPages = Math.ceil(filteredPodcasts.length / pageSize) || 1;
  const displayPodcasts = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredPodcasts.slice(start, start + pageSize);
  }, [filteredPodcasts, page, pageSize]);

  useEffect(() => {
    const tp = Math.ceil(filteredPodcasts.length / pageSize) || 1;
    if (page > tp) setPage(tp);
  }, [filteredPodcasts.length, page, pageSize]);

  const importablePodcasts = useMemo(
    () => podcasts.filter((p) => !p.isArchived),
    [podcasts],
  );

  const openImportDocumentFlow = useCallback(() => {
    if (importablePodcasts.length === 0) {
      toast.error(t('podcasts.pdfImport.noPodcast'));
      return;
    }
    if (importablePodcasts.length === 1) {
      setImportDocumentPodcast(importablePodcasts[0]!);
      return;
    }
    setImportPickerOpen(true);
  }, [importablePodcasts, t]);

  return (
    <div className={cn('space-y-3', embedded && 'px-6 py-6')} data-tour="tour-podcasts-root">
      {/* Header */}
      <div
        className={cn(
          'flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between',
          embedded && 'sm:justify-end',
        )}
      >
        {!embedded ? (
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_12%,transparent)]">
            <Mic2 className="h-4 w-4 text-[color:var(--inst-player-primary,#0C1C38)]" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-slate-900 dark:text-white">{t('podcasts.page.title')}</h1>
          </div>
          <span className="text-xs text-slate-400 dark:text-slate-500">
            {t('podcasts.page.totalCount', { count: filteredPodcasts.length })}
          </span>
        </div>
        ) : (
          <span className="text-xs text-slate-400 dark:text-slate-500 sm:mr-auto">
            {t('podcasts.page.totalCount', { count: filteredPodcasts.length })}
          </span>
        )}
        <div className="flex items-center gap-2">
          <Link
            to="/podcasts/episodes"
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm font-medium text-slate-700 dark:text-gray-300 hover:bg-slate-50 dark:hover:bg-gray-700 transition-colors"
          >
            <ListMusic className="h-3.5 w-3.5" />
            {t('podcasts.page.episodesLink')}
          </Link>
          <button
            type="button"
            onClick={openImportDocumentFlow}
            disabled={importablePodcasts.length === 0}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
            title={t('podcasts.pdfImport.openButton')}
          >
            <FileText className="h-3.5 w-3.5" />
            {t('podcasts.pdfImport.openButton')}
          </button>
          <button
            onClick={() => setModalPodcast('new')}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[color:var(--inst-player-primary,#0C1C38)] px-4 py-1.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-[color:var(--inst-player-primary-hover,#19335E)]"
          >
            <Plus className="h-3.5 w-3.5" />
            {t('podcasts.page.newButton')}
          </button>
        </div>
      </div>

      {/* Filtres & vues (aligné liste formations) */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-1 dark:bg-gray-800">
          {(['all', 'archived'] as const).map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => {
                setActiveFilter(key);
                setPage(1);
              }}
              className={cn(
                'rounded-md px-3 py-1.5 text-sm font-medium transition-all duration-200',
                activeFilter === key
                  ? 'bg-white text-slate-900 shadow-sm dark:bg-gray-700 dark:text-white'
                  : 'text-slate-600 hover:text-slate-900 dark:text-gray-400 dark:hover:text-white',
              )}
            >
              {key === 'all' ? t('podcasts.list.filterAll') : t('podcasts.list.filterArchived')}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <div className="w-64 min-w-0 shrink-0">
            <InstitutionStyledSearchInput
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder={t('podcasts.filters.searchPlaceholder')}
            />
          </div>

          <div className="flex items-center rounded-lg border border-slate-200 bg-white p-0.5 dark:border-gray-600 dark:bg-gray-800">
            <button
              type="button"
              onClick={() => setViewMode('grid')}
              className={cn(
                'rounded-md p-1.5 transition-all duration-200',
                viewMode === 'grid'
                  ? 'bg-slate-100 text-slate-900 dark:bg-gray-700 dark:text-white'
                  : 'text-slate-400 hover:text-slate-600 dark:text-gray-500 dark:hover:text-gray-300',
              )}
              title={t('podcasts.filters.viewGrid', 'Grille')}
              aria-pressed={viewMode === 'grid'}
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setViewMode('list')}
              className={cn(
                'rounded-md p-1.5 transition-all duration-200',
                viewMode === 'list'
                  ? 'bg-slate-100 text-slate-900 dark:bg-gray-700 dark:text-white'
                  : 'text-slate-400 hover:text-slate-600 dark:text-gray-500 dark:hover:text-gray-300',
              )}
              title={t('podcasts.filters.viewList')}
              aria-pressed={viewMode === 'list'}
            >
              <List className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setViewMode('categories')}
              className={cn(
                'rounded-md p-1.5 transition-all duration-200',
                viewMode === 'categories'
                  ? 'bg-slate-100 text-slate-900 dark:bg-gray-700 dark:text-white'
                  : 'text-slate-400 hover:text-slate-600 dark:text-gray-500 dark:hover:text-gray-300',
              )}
              title={t('podcasts.filters.viewCategories')}
              aria-pressed={viewMode === 'categories'}
            >
              <FolderTree className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div>
        {error && (
          <div className="mx-6 mt-4 flex items-center gap-3 rounded-xl border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/20 px-4 py-3">
            <AlertCircle className="h-5 w-5 flex-shrink-0 text-red-500" />
            <p className="flex-1 text-sm text-red-700 dark:text-red-300">{error}</p>
            <button onClick={loadPodcasts} className="text-sm font-medium text-red-700 dark:text-red-300 hover:text-red-800">{t('podcasts.actions.retry')}</button>
          </div>
        )}

        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-[72px] animate-pulse rounded-xl bg-gray-100 dark:bg-gray-800" />
            ))}
          </div>
        ) : podcasts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_10%,transparent)]">
              <Mic2 className="h-8 w-8" style={{ color: 'var(--inst-player-primary, #0C1C38)' }} />
            </div>
            <p className="mt-4 text-lg font-semibold text-gray-900 dark:text-white">{t('podcasts.list.emptyTitle')}</p>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('podcasts.list.emptySubtitle')}</p>
            <button
              onClick={() => setModalPodcast('new')}
              className="mt-4 flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium text-white transition-colors"
              style={{ background: 'var(--inst-player-primary, #0C1C38)' }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--inst-player-primary-hover, #19335E)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--inst-player-primary, #0C1C38)')}
            >
              <Plus className="h-4 w-4" />
              {t('podcasts.list.createFirst')}
            </button>
          </div>
        ) : viewMode === 'categories' ? (
          <div className="space-y-5">
            {categoryKeyForUi !== null && activeCategoryBrowse ? (
              <>
                <button
                  type="button"
                  onClick={() => setCategoryBrowseKey(null)}
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-gray-600 dark:bg-gray-800 dark:text-slate-200 dark:hover:bg-gray-700"
                >
                  <ChevronLeft className="h-4 w-4" />
                  {t('podcasts.list.categoryBack', 'Toutes les catégories')}
                </button>

                <div className="rounded-xl border border-slate-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-900">
                  {activeCategoryBrowse.category ? (
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                      <div className="relative h-36 w-full shrink-0 overflow-hidden rounded-xl bg-slate-100 dark:bg-gray-800 sm:h-32 sm:w-48">
                        {activeCategoryBrowse.category.coverImageUrl ? (
                          <OptimizedImage
                            priority
                            src={imagesApi.resolveUrl(activeCategoryBrowse.category.coverImageUrl) ?? ''}
                            alt=""
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div
                            className="flex h-full w-full items-center justify-center"
                            style={{
                              background: `linear-gradient(135deg, ${activeCategoryBrowse.category.color}55, ${activeCategoryBrowse.category.color}18)`,
                            }}
                          >
                            <FolderTree className="h-14 w-14 opacity-50" style={{ color: activeCategoryBrowse.category.color }} />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span
                            className="h-2.5 w-2.5 shrink-0 rounded-full"
                            style={{ backgroundColor: activeCategoryBrowse.category.color }}
                            aria-hidden
                          />
                          <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                            {activeCategoryBrowse.category.name}
                          </h2>
                        </div>
                        {activeCategoryBrowse.category.description ? (
                          <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
                            {activeCategoryBrowse.category.description}
                          </p>
                        ) : null}
                        <p className="mt-2 text-xs font-medium text-slate-500 dark:text-slate-500">
                          {t('podcasts.list.groupPodcastCount', { count: activeCategoryBrowse.podcasts.length })}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-wrap items-center gap-2">
                      <Tag className="h-5 w-5 text-slate-500 dark:text-gray-400" />
                      <h2 className="text-xl font-bold text-slate-900 dark:text-white">{t('podcasts.list.uncategorized')}</h2>
                      <span className="text-xs text-slate-500 dark:text-gray-400">
                        {t('podcasts.list.groupPodcastCount', { count: activeCategoryBrowse.podcasts.length })}
                      </span>
                    </div>
                  )}
                </div>

                {activeCategoryBrowse.podcasts.length === 0 ? (
                  <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50/80 px-4 py-8 text-center text-sm text-slate-600 dark:border-gray-600 dark:bg-gray-900/40 dark:text-slate-400">
                    {t('podcasts.list.categoryEmptyFiltered', 'Aucun podcast dans cette vue (recherche active).')}
                  </p>
                ) : (
                  <div className="flex flex-col gap-3">
                    {activeCategoryBrowse.podcasts.map((p) => (
                      <PodcastTrainerListRow
                        key={p.id}
                        podcast={p}
                        categoryLabel={resolvePodcastCategoryLabel(p, categoryNameById, t('podcasts.list.uncategorized'))}
                        showCategoryChip={false}
                        t={t}
                        i18n={i18n}
                        onCoverLightbox={(url, title) => setCoverLightbox({ url, title })}
                        onPreview={() => setPreviewPanel(p)}
                        onContents={() => openPodcastWorkspace(p)}
                        onImportDocument={() => setImportDocumentPodcast(p)}
                        onShare={() => setSharePodcast(p)}
                        onOpenOffer={isLyrraOne() ? () => setOfferPodcast(p) : undefined}
                        onEdit={() => openPodcastSettings(p)}
                        onToggleActive={() => void handleToggleActive(p)}
                        onArchive={() => void handleArchivePodcast(p)}
                        onRestoreArchive={() => void handleRestoreArchivedPodcast(p)}
                        onDelete={() => setDeleteConfirm(p)}
                        onOpenDashboard={() => openPodcastDashboard(p)}
                      />
                    ))}
                  </div>
                )}
              </>
            ) : (
              <>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {t('podcasts.list.categoryBrowseHint', 'Choisissez une catégorie pour afficher ses podcasts.')}
                </p>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  {groupedByCategory.groups.map((group) => (
                    <button
                      key={group.category.id}
                      type="button"
                      onClick={() => setCategoryBrowseKey(group.category.id)}
                      className="group flex h-full flex-col overflow-hidden rounded-xl border border-slate-200 bg-white text-start shadow-sm transition-all hover:border-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_40%,transparent)] hover:shadow-md dark:border-gray-700 dark:bg-gray-900"
                    >
                      <div className="relative h-44 w-full overflow-hidden bg-slate-100 dark:bg-gray-800">
                        {group.category.coverImageUrl ? (
                          <OptimizedImage
                            src={imagesApi.resolveUrl(group.category.coverImageUrl) ?? ''}
                            alt=""
                            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                          />
                        ) : (
                          <div
                            className="flex h-full w-full items-center justify-center"
                            style={{
                              background: `linear-gradient(135deg, ${group.category.color}55, ${group.category.color}18)`,
                            }}
                          >
                            <FolderTree className="h-12 w-12 opacity-45" style={{ color: group.category.color }} />
                          </div>
                        )}
                      </div>
                      <div className="flex flex-1 flex-col p-4">
                        <div className="flex items-center gap-2">
                          <span
                            className="h-2 w-2 shrink-0 rounded-full"
                            style={{ backgroundColor: group.category.color }}
                            aria-hidden
                          />
                          <h3 className="font-semibold text-slate-900 dark:text-white">{group.category.name}</h3>
                        </div>
                        {group.category.description ? (
                          <p className="mt-2 line-clamp-3 text-sm text-slate-600 dark:text-slate-400">
                            {group.category.description}
                          </p>
                        ) : null}
                        <p className="mt-auto pt-3 text-xs font-semibold text-[color:var(--inst-player-primary,#0C1C38)]">
                          {t('podcasts.list.groupPodcastCount', { count: group.podcasts.length })}
                        </p>
                      </div>
                    </button>
                  ))}

                  {groupedByCategory.uncategorized.length > 0 ? (
                    <button
                      type="button"
                      onClick={() => setCategoryBrowseKey(CATEGORY_BROWSE_UNCATEGORIZED)}
                      className="group flex h-full flex-col overflow-hidden rounded-xl border border-dashed border-slate-300 bg-slate-50/80 text-start shadow-sm transition-all hover:border-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_45%,transparent)] hover:bg-white dark:border-gray-600 dark:bg-gray-900/50 dark:hover:bg-gray-900"
                    >
                      <div className="relative flex h-44 w-full items-center justify-center bg-slate-100 dark:bg-gray-800/80">
                        <Tag className="h-12 w-12 text-slate-400 dark:text-gray-500" />
                      </div>
                      <div className="flex flex-1 flex-col p-4">
                        <h3 className="font-semibold text-slate-800 dark:text-gray-100">{t('podcasts.list.uncategorized')}</h3>
                        <p className="mt-2 line-clamp-2 text-sm text-slate-600 dark:text-slate-400">
                          {t('podcasts.list.uncategorizedCardHint', 'Podcasts sans catégorie assignée.')}
                        </p>
                        <p className="mt-auto pt-3 text-xs font-semibold text-[color:var(--inst-player-primary,#0C1C38)]">
                          {t('podcasts.list.groupPodcastCount', { count: groupedByCategory.uncategorized.length })}
                        </p>
                      </div>
                    </button>
                  ) : null}
                </div>
              </>
            )}
          </div>
        ) : filteredPodcasts.length === 0 ? (
          <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50/80 px-4 py-12 text-center text-sm text-slate-600 dark:border-gray-600 dark:bg-gray-900/40 dark:text-slate-400">
            {activeFilter === 'archived'
              ? t('podcasts.list.emptyArchivedNone')
              : t('podcasts.list.emptyAllArchived')}
          </p>
        ) : (
          <div
            className={cn(
              viewMode === 'grid'
                ? 'grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
                : 'flex flex-col gap-3',
            )}
          >
            {displayPodcasts.map((p) => {
              const label = resolvePodcastCategoryLabel(p, categoryNameById, t('podcasts.list.uncategorized'));
              const rowHandlers = {
                onCoverLightbox: (url: string, title: string) => setCoverLightbox({ url, title }),
                onPreview: () => setPreviewPanel(p),
                onContents: () => openPodcastWorkspace(p),
                onImportDocument: () => setImportDocumentPodcast(p),
                onShare: () => setSharePodcast(p),
                onOpenOffer: isLyrraOne() ? () => setOfferPodcast(p) : undefined,
                onEdit: () => openPodcastSettings(p),
                onToggleActive: () => void handleToggleActive(p),
                onArchive: () => void handleArchivePodcast(p),
                onRestoreArchive: () => void handleRestoreArchivedPodcast(p),
                onDelete: () => setDeleteConfirm(p),
                onOpenDashboard: () => openPodcastDashboard(p),
              };
              return viewMode === 'grid' ? (
                <PodcastTrainerGridCard
                  key={p.id}
                  podcast={p}
                  categoryLabel={label}
                  t={t}
                  i18n={i18n}
                  {...rowHandlers}
                />
              ) : (
                <PodcastTrainerListRow
                  key={p.id}
                  podcast={p}
                  categoryLabel={label}
                  showCategoryChip
                  t={t}
                  i18n={i18n}
                  {...rowHandlers}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Pagination (liste / grille uniquement) */}
      {viewMode !== 'categories' && totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500 dark:text-gray-400">
            {t('podcasts.pagination.totalSummary', { count: filteredPodcasts.length })}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="rounded-xl border border-gray-200 dark:border-gray-700 p-1.5 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm text-gray-700 dark:text-gray-300">{t('podcasts.pagination.pageOf', { page, total: totalPages })}</span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="rounded-xl border border-gray-200 dark:border-gray-700 p-1.5 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 transition-colors"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {coverLightbox &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="fixed inset-0 z-[100] flex flex-col bg-black/92 backdrop-blur-sm"
            role="dialog"
            aria-modal="true"
            aria-label={t('podcasts.lightbox.ariaDialog')}
          >
            <button
              type="button"
              className="absolute inset-0 z-0 cursor-zoom-out"
              aria-label={t('podcasts.lightbox.ariaClose')}
              onClick={() => setCoverLightbox(null)}
            />
            <div className="relative z-10 flex min-h-0 flex-1 items-center justify-center px-2 py-16 sm:px-4">
              <img
                src={coverLightbox.url}
                alt={coverLightbox.title}
                className="max-h-[min(85vh,100dvh-8rem)] max-w-[min(100vw-4rem,1400px)] object-contain shadow-2xl"
              />
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setCoverLightbox(null);
              }}
              className="absolute end-3 top-3 z-[60] flex h-12 w-12 items-center justify-center rounded-full border-2 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-[0_4px_24px_rgba(0,0,0,0.65)] transition-colors hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-900 sm:end-4 sm:top-4"
              aria-label={t('podcasts.lightbox.ariaClose')}
            >
              <X className="h-7 w-7" />
            </button>
          </div>,
          document.body,
        )}

      {/* Modals & Panels */}
      {modalPodcast === 'new' && (
        <PodcastModal
          podcast={null}
          categories={categories}
          defaultCategoryId={
            viewMode === 'categories' &&
            categoryBrowseKey &&
            categoryBrowseKey !== CATEGORY_BROWSE_UNCATEGORIZED &&
            PODCAST_CATEGORY_UUID_RE.test(categoryBrowseKey)
              ? categoryBrowseKey
              : undefined
          }
          onClose={() => setModalPodcast(null)}
          onSaved={(saved) => {
            void loadPodcasts();
            if (!saved) return;
            if (saved.creationMode === 'simple') {
              navigate(getPodcastEditPath(saved));
            } else {
              openPodcastWorkspace(saved);
            }
          }}
        />
      )}

      {editPanelPodcast && (
        <PodcastEditPanel
          podcast={editPanelPodcast}
          categories={categories}
          onClose={() => setEditPanelPodcast(null)}
          onSaved={loadPodcasts}
        />
      )}

      {previewPanel && (
        <PodcastPreviewPanel
          podcast={previewPanel}
          onClose={() => setPreviewPanel(null)}
        />
      )}

      {contentsPanel && (
        <ContentsPanel
          podcast={contentsPanel}
          onClose={() => setContentsPanel(null)}
          onChanged={loadPodcasts}
        />
      )}

      {deleteConfirm && (
        <DeleteConfirmDialog
          podcast={deleteConfirm}
          onCancel={() => setDeleteConfirm(null)}
          onConfirm={() => handleDelete(deleteConfirm)}
        />
      )}

      {sharePodcast && (
        <SharePodcastModal
          podcast={sharePodcast}
          onClose={() => setSharePodcast(null)}
        />
      )}

      {offerPodcast && isLyrraOne() && (
        <PublishPodcastOfferPanel
          podcastId={offerPodcast.id}
          podcastTitle={offerPodcast.title}
          podcastDescription={offerPodcast.description}
          podcastCoverImageUrl={offerPodcast.coverImageUrl}
          isActive={offerPodcast.isActive}
          isArchived={Boolean(offerPodcast.isArchived)}
          onClose={() => setOfferPodcast(null)}
        />
      )}

      {importPickerOpen ? (
        <PodcastImportPickerModal
          podcasts={importablePodcasts}
          onSelect={(p) => {
            setImportPickerOpen(false);
            setImportDocumentPodcast(p);
          }}
          onClose={() => setImportPickerOpen(false)}
        />
      ) : null}

      {importDocumentPodcast ? (
        <PodcastPdfImportModal
          podcast={importDocumentPodcast}
          onClose={() => setImportDocumentPodcast(null)}
          onImported={() => {
            void loadPodcasts();
            setImportDocumentPodcast(null);
          }}
        />
      ) : null}
    </div>
  );
}
